// leanbot_ble.js
// SDK Leanbot BLE - Quản lý kết nối và giao tiếp BLE với Leanbot

import { LeanbotCompiler } from "./LeanbotCompiler.js";
import { sleep } from "./helpers.js"

export class LeanbotBLE {

  static #config = null;

  static setConfig(config) {  
    LeanbotBLE.#config = config;
    Serial.setConfig(config.Serial);
    Uploader.setConfig(config.EspUploader);
    JDYUploader.setConfig(config.JDYUploader);
  }

  // ---- PRIVATE MEMBERS ----
  #device  = null;
  #server  = null;
  #service = null;
  #chars   = {};
  #requestStartMs = 0;
  #requestEndMs   = 0;
  #connectingStartMs = 0;
  #connectingEndMs   = 0;
  #disconnectStartMs = 0;
  #disconnectEndMs =   0;
  #setupStartMs = 0;

  #currentCompilePromise = null;

  BluetoothApiNotSupported = false;
  SecureContextMissing = false;
  BluetoothAdapterNotAvailable = false;

  // ---------------- BLE CORE ----------------

  async #returnBleResult(success, exception) {
    if (!success) {
      await this.onConnectError(exception);
    }
    return { success, exception };
  };

  async connect(deviceName = null) {

    this.#connectingStartMs = performance.now();
    this.#connectingEndMs = 0;
    try {

      if (this.SecureContextMissing) {
        return this.#returnBleResult(
          false,
          "Web Bluetooth requires a secure context. Open IDE via HTTPS or http://localhost."
        );
      }

      if (this.BluetoothApiNotSupported) { // auto failed when not supported
        return this.#returnBleResult(
          false,
          "Web Bluetooth API is unavailable in this browser. Please use Chrome/Edge on desktop and check Bluetooth permissions."
        );
      }

      if(this.BluetoothAdapterNotAvailable){
      return this.#returnBleResult(
          false,
          "Bluetooth not available on this device"
        );
      }

      // Mặc định: quét tất cả thiết bị có service UUID tương ứng
      const lbDeviceFilters = { services: [LeanbotBLE.#config.SERVICE_UUID] };
      const lbName = deviceName?.trim();
      let scanMessage = 'Scanning for Leanbots...';

      const nameWithoutBLE = (name) => name.replace(" BLE", "");

      // Nếu có deviceName → chỉ quét thiết bị có tên trùng khớp
      if (lbName) {
        lbDeviceFilters.name = lbName;
        scanMessage = `Looking for ${nameWithoutBLE(lbName)}...`;
      }

      this.#requestStartMs = performance.now();
      this.#requestEndMs = 0;

      await this.onScanBegin(scanMessage);

      this.#device = await globalThis.navigator.bluetooth.requestDevice({
        filters: [lbDeviceFilters],
      });

      await this.onSelect(nameWithoutBLE(this.#device.name));

      this.#requestEndMs = performance.now();

      // Thiết lập kết nối BLE
      await this.#setupConnection();
      return this.#returnBleResult(true, null);
    } catch (error) {
      if (String(error.message).toLowerCase() === "user cancelled the requestdevice() chooser."){
        // Web Bluetooth throws an error when user cancels the device picker.
        // This is expected behavior -> trigger its own callback then return a non-success result.
        await this.onCancel(error.message);
        return {success: false, exception: "cancelled"};
      }
      return this.#returnBleResult(false, `Connection failed: ${error.message || "Unknown error"}`);
    }
  }

  async reconnect() {
    this.#connectingStartMs = performance.now();
    this.#connectingEndMs = 0;
    try {
      if (this.isConnected()) {
        // Nếu đang kết nối rồi thì không cần làm gì
        return this.#returnBleResult(true, null);
      }

      if (this.#device) {
        // Nếu đã ngắt kết nối thì kết nối lại
        await this.#setupConnectionWithTimeoutAndRetry();
        return this.#returnBleResult(true, null);
      }

      // Gọi lại Connect nếu không có thiết bị trong phiên làm việc hiện tại
      return await this.connect(this.getLeanbotID());
    } catch (error) {
      return this.#returnBleResult(false, `Reconnect failed: ${error.message || "Unknown error"}`); 
    }
  }

  async disconnect() {
    this.#disconnectStartMs = performance.now();
    this.#disconnectEndMs = 0;
    try {
      // Không có thiết bị nào được lưu
      if (!this.#device) {
        return this.#returnBleResult(false, "No device found to disconnect. Please connect a device first.");
      }

      // Thiết bị tồn tại nhưng chưa kết nối
      if (!this.#device.gatt.connected) {
        return this.#returnBleResult(false, "Device is not currently connected.");
      }

      // Ngắt kết nối
      this.#device.gatt.disconnect();
      return this.#returnBleResult(true, null);
    } catch (error) {
      return this.#returnBleResult(false, `Disconnect failed: ${error.message || "Unknown error"}`);  
    }
  }

  isConnected() {
    return this.#device?.gatt.connected === true;
  }

  isDeviceExist(){
    return this.#device !== null;
  }

  getLeanbotID() {
    if (this.#device) return this.#device.name;
      
    const lastDevice = localStorage.getItem(LeanbotBLE.#config.LS_KEY_LAST_DEVICE);
    return lastDevice ? JSON.parse(lastDevice) : null;
  }

  clearLeanbotID(){
    this.#device = null;
    localStorage.removeItem(LeanbotBLE.#config.LS_KEY_LAST_DEVICE); // remove from local storage
  }

  isJDYResetNano(){
    return this.JDYUploader.isResetNano;
  }

  #onGattDisconnected = async () => {
    this.Uploader.isUploadSessionActive = false;
    this.#disconnectEndMs = performance.now();

    if(!this.isJDYResetNano()){
      await this.onDisconnect();
    }
    else{
      await this.Uploader.onJDYDisconnect();
    }

    // if (this.Uploader.isTransferring === true) {
    //   await this.Uploader.emitTransferError("Device disconnected while uploading.");
    //   this.Uploader.abortAll();
    // }
  };

  async #setupConnection(retryCount = 0) {
    this.#setupStartMs = performance.now();
    this.#connectingEndMs = 0;

    /** ---------- DISCONNECT EVENT ---------- */
    console.log("Callback onDisconnect: Enabled");

    this.#device.removeEventListener("gattserverdisconnected", this.#onGattDisconnected);

    this.#device.addEventListener("gattserverdisconnected", this.#onGattDisconnected);

    await sleep(0);  // Yield before enter time-critical gatt.connect()

    /** ---------- GATT CONNECTION ---------- */
    
    const t1 = performance.now();
    // this.#server = await this.#device.gatt.connect();
    try{
      this.#server = await this.#device.gatt.connect();
    }
    catch (e){
      const error = new Error(
        `gatt.connect() exception: ${e?.message || e}`
      );
      error.type = "gatt.connect.Error";
      throw error;
    }
    const t2 = performance.now(); 
    console.log(`[SETUP CONNECT] GATT connection established in ${(t2 - t1).toFixed(2)} ms`);
    
    const t3 = performance.now();
    // this.#service = await this.#server.getPrimaryService(LeanbotBLE.#config.SERVICE_UUID);
    try{
      this.#service = await this.#server.getPrimaryService(LeanbotBLE.#config.SERVICE_UUID);
    }
    catch (e){
      const error = new Error(
        `server.GetPrimaryService() exception: ${e?.message || e}`
      );
      error.type = "server.GetPrimaryService.Error";
      throw error;
    }
    const t4 = performance.now();
    console.log(`[SETUP CONNECT] Service discovery completed in ${(t4 - t3).toFixed(2)} ms`);

    /** ---------- CHARACTERISTICS ---------- */
    const t5 = performance.now();
    const chars = await this.#service.getCharacteristics();
    const t6 = performance.now();
    console.log(`[SETUP CONNECT] Characteristics discovery completed in ${(t6 - t5).toFixed(2)} ms`);

    this.#chars = {};
    for (const c of chars) this.#chars[c.uuid.toLowerCase()] = c;
    
    /** ---------- SETUP SUB-CONNECTIONS ---------- */
    const t7 = performance.now();
    await this.Serial.setupConnection(this.#chars);
    const t8 = performance.now();
    console.log(`[SETUP CONNECT] Serial setup completed in ${(t8 - t7).toFixed(2)} ms`);

    const t9 = performance.now();
    await this.Uploader.setupConnection(this.#chars, LeanbotBLE.#config.EspUploader.BLE_MaxLength, LeanbotBLE.#config.EspUploader.BLE_Interval);
    const t10 = performance.now();
    console.log(`[SETUP CONNECT] Uploader setup completed in ${(t10 - t9).toFixed(2)} ms`);

    /** ---------- CONNECT CALLBACK ---------- */

    console.log("Callback onConnect: Enabled");

    this.#connectingEndMs = performance.now();

    if(!this.isJDYResetNano()){
      await this.onConnect();
    }
    else{
      await this.Uploader.onJDYConnect(retryCount);
    }
    
    /** --------- SAVE DEVICENAME TO LOCALSTORAGE --------- */
    if(this.#device.name !== localStorage.getItem(LeanbotBLE.#config.LS_KEY_LAST_DEVICE)){ // Only save to localStorage if device name is different from previous to reduce unnecessary writes
      console.log("Saving device to localStorage:", this.#device.name);
      localStorage.setItem(LeanbotBLE.#config.LS_KEY_LAST_DEVICE, JSON.stringify(this.#device.name));  // stringify() to handle device.name = null
    }
  }

  async #setupConnectionWithTimeoutAndRetry(){ // multiple setup connect tries within 6s
    const retryReconnect = async (retry) => {

      try{
        await this.#setupConnection(retry);
        if(this.isConnected()){
          console.log(`reconnect attempt ${retry} success`);
          return true;
        }
        throw new Error(`Device not connected`); // throw error to trigger catch block and retry
      }
      catch(e){
        const message = e?.message || e;
        console.log(`reconnect attempt ${retry} failed:`, message);
        this.#device.gatt.disconnect(); // attemp to disconnect to terminate current attempt connection (no callback)
       
        // if(e.name === "NetworkError"){
        //   throw e; // stop immediately if gatt.connect()/server.GetPrimaryService() error
        // }

        // stop immediately if gatt.connect()/server.GetPrimaryService() error (no retry)
        if (
          e.type === "gatt.connect.Error" ||
          e.type === "server.GetPrimaryService.Error"
        ) {
          throw e;
        }

        return false;
      }
    }

    // await retryUntilTimeout(retryReconnect, "retry Reconnect", 6000, LeanbotBLE.#config.reconnectInterval); // keep trying reconnect until 6s

    const RETRY_TIMEOUT = 6000;
    try{
      await retryUntilTimeout(retryReconnect, "retry Reconnect", RETRY_TIMEOUT, LeanbotBLE.#config.reconnectInterval); // keep trying reconnect until 6s
    }
    catch(e){

      // Based on previous investigations, most reconnect timeouts are caused by
      // gatt.connect(). Treat the overall reconnect timeout as a gatt.connect()
      // failure so the caller can follow the corresponding error-handling path.
      if (e instanceof Error && e.type === "retryUntilTimeout.Timeout"){
        throw new Error(`gatt.connect() timeout after ${RETRY_TIMEOUT} ms`);
      }

      // Propagate other errors unchanged.
      throw e;
    }
  }

  async compileAndUpload2(SourceCode, compileServer){

    let uploadCompileInfo = null;

    const compilePromise = this.Compiler.compile(SourceCode, compileServer);

    this.#currentCompilePromise = compilePromise;

    compilePromise.then(result => { // take compile result when done
      uploadCompileInfo = result.log;
    });

    compilePromise.finally(() => {
      this.#currentCompilePromise = null; // cleanup when done
    });

    const result = await this.JDYUploader.jdyUpload2(compilePromise);
    result.uploadCompileInfo = uploadCompileInfo;
    return result;
  }

  async uploadFromHexText(hextext){
    const preCompiledHexText = Promise.resolve({
      success: true,
      hexText: hextext,
      log: ""
    });

    const result = await this.JDYUploader.jdyUpload2(preCompiledHexText);
    return result;
  }

  async uploadFlush(){
    if (this.#currentCompilePromise) {
      try{await this.#currentCompilePromise;}catch(e){} /// tránh throw (nếu có)
    }
  }

  async checkWebBluetoothAvailability(){
    this.BluetoothApiNotSupported  =
    !(
      typeof globalThis.navigator !== "undefined" &&
      !!globalThis.navigator.bluetooth &&
      typeof globalThis.navigator.bluetooth.requestDevice === "function"
    );

    this.SecureContextMissing = !(globalThis.isSecureContext === true);

    if(this.BluetoothApiNotSupported){
      return;
    }

    const bluetoothAvailable = await navigator.bluetooth.getAvailability();
    this.BluetoothAdapterNotAvailable = !bluetoothAvailable;
  }

  constructor() {

    if (!LeanbotBLE.#config) {
      throw new Error("Missing LeanbotBLE #config");
    }
    
    this.onConnect = async () => {};
    this.onDisconnect = async () => {};
    this.onConnectError = async () => {};
    this.onSelect = async () => {};
    this.onCancel = async () => {};
    this.onScanBegin = async () => {};
    this.onJDYprepareUploadError = async () => {};

    this.Serial      = new Serial(this);
    this.Uploader    = new Uploader(this);
    this.JDYUploader = new JDYUploader(this, this.Serial, this.Uploader);
    this.Compiler    = new LeanbotCompiler();
    
    this.Serial.setJDYUploader(this.JDYUploader);
    this.Uploader.setJDYUploader(this.JDYUploader);
  }

  setupConnectTimeMs() {
    if (this.#connectingEndMs === 0){
      return performance.now() - this.#setupStartMs;
    }
    return this.#connectingEndMs - this.#setupStartMs;
  }

  connectingTimeMs() {
    if (this.#connectingEndMs === 0){
      return performance.now() - this.#connectingStartMs;
    }
    return this.#connectingEndMs - this.#connectingStartMs;
  }

  requestTimeMs(){
    if (this.#requestEndMs === 0){
      return performance.now() - this.#requestStartMs;
    }
    return this.#requestEndMs - this.#requestStartMs;
  }

  disconnectingTimeMs() {
    if (this.#disconnectEndMs === 0){
      return performance.now() - this.#disconnectStartMs;
    }
    return this.#disconnectEndMs - this.#disconnectStartMs;
  }

}

// ======================================================
// 🔹 SUBMODULE: SERIAL
// ======================================================
class Serial {
  
  static #config = null;

  static setConfig(config){
    Serial.#config = config;
  }

  #SerialPipe_char = null;
  #JDYUploader = null;

  constructor(){
    if (!Serial.#config) { 
      throw new Error("Missing Serial #config");
    }
  }

  setJDYUploader(jdyUploader) {
    this.#JDYUploader = jdyUploader;
  }

  /** Kiểm tra hỗ trợ Serial */
  isSupported() {
    return !!this.#SerialPipe_char;
  }

  /** Callback khi nhận notify Serial */
  onMessage = () => {};

  /** Callback chuyển tiếp dữ liệu thô sang JDYUploader */
  onForwardReponse = async () => {};

  // Queue nhận dữ liệu
  #SerialPipe_rxQueue   = [];
  #SerialPipe_rxTSQueue = [];
  #SerialPipe_busy   = false;
  #SerialPipe_buffer = "";
  #SerialPipe_lastTS = null;

  /** Gửi dữ liệu qua đặc tính Serial mặc định (UUID)
   * @param {string|Uint8Array} data - dữ liệu cần gửi
   * @param {boolean} withResponse - true = gửi chờ phản hồi, false = gửi nhanh
   */
  async send(data, withResponse = true) {
    try {
      if (!this.isSupported()) {
        console.log("Serial.Send Error: Serial not supported");
        return;
      }
      // Chuyển dữ liệu sang Uint8Array nếu là chuỗi
      const buffer = typeof data === "string" ? new TextEncoder().encode(data) : data;
      await this.SerialPipe_sendToLeanbot(buffer, withResponse);
    } catch (e) {
      console.log(`Serial.Send Error: ${e}`);
    }
  }

  #onCharacteristicChanged = (event) => {
    const bytes = new Uint8Array(event.target.value.buffer); // UploaderJDY needs raw bytes
    if (this.#JDYUploader && this.#JDYUploader.isUploading) {
      void this.onForwardReponse(bytes).catch((err) => {
        console.log("[UPLOAD] Forward response error:", err);
      });
      return;
    }
    const BLEPacket = new TextDecoder().decode(bytes);
    const Packet_TS = new Date(performance.timeOrigin + event.timeStamp);
    void this.#SerialPipe_onReceiveFromLeanbot(BLEPacket, Packet_TS).catch((err) => {
      console.log("Serial receive handler error:", err);
    });
  };

  /** Thiết lập characteristic + notify **/
  async setupConnection(characteristics) {
    const fullUUID  = Serial.#config.SerialPipe_UUID;
    const shortUUID = fullUUID.slice(4, 8); // ffe1

    let char = null;

    if (characteristics[fullUUID]) {
      char = characteristics[fullUUID];
    } else {
      console.log("SerialPipe NOT found by full UUID");
    }

    if (!char && characteristics[shortUUID]) {
      char = characteristics[shortUUID];
    } else if (!char) {
      console.log("SerialPipe NOT found by short UUID");
    }

    this.#SerialPipe_char = char;

    if (!this.isSupported()) {
      console.log("Serial: Not supported");
      return;
    }

    console.log("Serial: supported");


    if (!this.isSupported()) {
      console.log("Serial: Not supported");
      return;
    }

    if (!this.#SerialPipe_char.properties.notify) {
      console.log("Serial Notify: Not supported");
      return;
    }


    this.#SerialPipe_char.removeEventListener("characteristicvaluechanged", this.#onCharacteristicChanged);
    await this.#SerialPipe_char.startNotifications();
    this.#SerialPipe_char.addEventListener("characteristicvaluechanged", this.#onCharacteristicChanged);

    console.log("Callback Serial.onMessage: Enabled");
  }

  #SerialPipe_rxQueueHandler() {
    if (this.#SerialPipe_busy) return;
    this.#SerialPipe_busy = true;

    while (this.#SerialPipe_rxQueue.length > 0) {
      const BLEPacket = this.#SerialPipe_rxQueue.shift();
      this.#SerialPipe_buffer += BLEPacket;

      const PacketTS  = this.#SerialPipe_rxTSQueue.shift();

      let lines = this.#SerialPipe_buffer.split("\n");
      this.#SerialPipe_buffer = lines.pop();

      for (let i = 0; i < lines.length; i++) { 
        let line = lines[i] + "\n";
        let timeGapMs = this.#SerialPipe_lastTS ? (PacketTS - this.#SerialPipe_lastTS) : 0;

        if (line === "AT+NAME\r\n")  continue;
        if (line === "LB999999\r\n") {
          line = ">>> Leanbot ready >>>\n";
          this.onMessage(line, PacketTS, timeGapMs);
          this.onMessage("\n", PacketTS, 0);
          continue;
        }

        this.onMessage(line, PacketTS, timeGapMs);
        this.#SerialPipe_lastTS = PacketTS;
      }
    }

    this.#SerialPipe_busy = false;
  }

  // ========== Serial Pipe Communication ==========
  #uploadWriteRecords = []; // save information of everytime a data is send to leanbot (only use for debug)

  clearUploadWriteRecords(){
    this.#uploadWriteRecords = [];
  }

  getUploadWriteRecords(){

    const toISOStringLocal = (ts) => {
      const d = new Date(ts);
      const ms = String(d.getMilliseconds()).padStart(3, '0');
      return d.toLocaleString('sv', {timeZoneName: 'longOffset'}).replace(' ', 'T').replace(' GMT', '').replace(/([\+\-]\d{2}:\d{2})$/, `.${ms}$1`);
    };

    return this.#uploadWriteRecords
      .slice(0, 3)  // lấy 3 record mới nhất (index 0 → 2)
      .map(r => {
        const page     = r.pageIndex > -1 ? `.${r.pageIndex}` : '';
        const rp       = r.withResponse    ? '.RP,'            : '';
        const start    = toISOStringLocal(r.startSend);
        const end      = toISOStringLocal(r.sendEnd);
        return `${r.command}${page}${rp}[${r.packetLength}] = [${start}, ${end}]`;
      })
      .join('\n');
  }

  async SerialPipe_sendToLeanbot(packet, withResponse = true) {
    const startSend = Date.now();
    try{
      if (withResponse) {
        // await this.#SerialPipe_char.writeValue(packet); // write value is deprecated
        await this.#SerialPipe_char.writeValueWithResponse(packet);
        // ref: https://developer.mozilla.org/en-US/docs/Web/API/BluetoothRemoteGATTCharacteristic/writeValue
      } else {
        await this.#SerialPipe_char.writeValueWithoutResponse(packet);
      }
    }
    catch(e){
      console.log('SerialPipe_sendToLeanbot error: ', e?.message || e);
      throw e; // re-throw
    }
    finally{
      const sendEnd = Date.now();
      if(!this.#JDYUploader.isUploading)return;
      const packetLength = packet.length;
      let command = 'u'; // unknown 
      let pageIndex = -1;

      const getpageIndex = (packet) => {
        // loadAddressCmd = [0x55, addrLow, addrHigh, 0x20]
        const wordAddress = (packet[2] << 8) | packet[1]; // addrHigh at [2], addrLow at [1]
        const pageIndex = Math.floor(wordAddress * 2 / 128);
        return pageIndex;
      }

      switch(packet.length){
        case 120:
          command = 'w' // send write first half
          pageIndex = getpageIndex(packet); // take page number from load address command
          break;
        case 17: {
          command = 'w' // send write second half 
          const candidateW1s = this.#uploadWriteRecords
            .filter(r =>
            r.packetLength === 120       &&   // là w half 1
            r.startSend    <  startSend  &&   // gửi trước w2 hiện tại
            !this.#uploadWriteRecords.some(r2 =>  // chưa có w half 2 tương ứng
              r2.packetLength === 17          &&
              r2.pageIndex    === r.pageIndex &&
              r2.startSend    >  r.startSend  &&
              r2.startSend    <  startSend
            )
          )
          .sort((a, b) => a.pageIndex - b.pageIndex); // pageIndex tăng dần
          pageIndex = candidateW1s[0]?.pageIndex ?? -1;
          break;
        }
        case 9:
          command = 'r' // send read command
          pageIndex = getpageIndex(packet);
          break;
        case 2:
          command = 's' // send getSync
          break;
      }

      this.#uploadWriteRecords.unshift({command, pageIndex, packetLength, startSend, sendEnd, withResponse});
    }
  }

  async #SerialPipe_onReceiveFromLeanbot(BLEPacket, Packet_TS){
    this.#SerialPipe_rxQueue.push(BLEPacket);
    this.#SerialPipe_rxTSQueue.push(Packet_TS);
    setTimeout(() => this.#SerialPipe_rxQueueHandler(), 0);
  }
}

// ======================================================
// 🔹 SUBMODULE: UPLOADER
// ======================================================
class Uploader {

  static #config = null;

  static setConfig(config){
    Uploader.#config = config;
  }

  static getBLE_MaxLength(){
    return Uploader.#config.BLE_MaxLength;
  }

  #leanbot = null;
  #JDYUploader = null;

  setJDYUploader(jdyUploader) {
    this.#JDYUploader = jdyUploader;
  }

  // Characteristics
  #DataPipe_char     = null;
  #ControlPipe_char  = null;

  // Upload state
  #packets            = [];
  #packetHashes      = [];
  #nextToSend        = 0;
  #lastReceived      = -1;
  totalBytesData     = 0;
  totalPackets       = 0;
  #PacketBufferSize  = 0;
  
  // Queue state
  #ControlPipe_rxQueue = [];
  #ControlPipe_busy = false;

  // ===== User Callbacks =====
  onMessage  = () => {};
  onTransfer = () => {};
  onWrite    = () => {};
  onVerify   = () => {};
  onRSSI     = () => {};
  onSuccess  = async () => {};
  onError    = async () => {};
  onCancelUpload  = async () => {};
  onUploadAbnormal = async () => {};
  
  isTransferring  = null;
  onTransferError = () => {};
  onWriteError    = () => {};
  onVerifyError   = () => {};

  isUploadSessionActive = null;

  #uploadStartMs = 0;
  #uploadEndMs = 0;

  // only jdy
  onEnterBootloader = async () => {};
  // onJDYWrite        = async () => {};
  // onJDYRead         = async () => {};
  onJDYConnect      = async () => {};
  onJDYDisconnect   = async () => {};

  constructor(ble) {
    if (!Uploader.#config) {
      throw new Error("Missing EspUploader #config");
    }
    this.#leanbot = ble;
  }

  /** Kiểm tra hỗ trợ Uploader */
  isSupported() {
    return !!this.#DataPipe_char && !!this.#ControlPipe_char;
  }

  /** Prepare for upload */
  async prepareUpload() {

    // First session -> user must successfully select & connect a device
    let isFirstSession = false;
    if(!this.#leanbot.getLeanbotID()){
      isFirstSession = true;
      const result = await this.#leanbot.connect();
      if(!result.success)return false; // hard abort
    }

    // Device known => choose device-specific preparation
    if (!this.isSupported()){ // JDY33
      return await this.#JDYUploader.prepareUpload(isFirstSession); 
    }

    // LbEsp32
    if(isFirstSession)return true;
    const result = await this.#leanbot.reconnect();
    return result.success;
  }

  /** Upload HEX */
  async upload(hexText) {
    try{
      this.#uploadStartMs = performance.now();
      this.#uploadEndMs = 0;

      if (!this.isSupported() && this.#JDYUploader) {
        await this.#JDYUploader.upload(hexText);
        return;
      }

      this.isUploadSessionActive = true;
    
      // Chuyển toàn bộ HEX sang gói BLE
      this.#packets = convertHexToBlePackets(hexText);
      this.totalPackets = this.#packets.length - 1;

      // Tính toán hash cho từng gói
      this.#packetHashes = [];
      this.#computePacketHashesHash32();

      const totalBytes = this.#packets.reduce((a, p) => a + p.length, 0);
      const dataBytes = totalBytes - this.#packets.length - 1; // trừ đi header (1 byte) và EOF block (1 byte)
      this.totalBytesData = Math.ceil(dataBytes / 128) * 128; // Làm tròn lên bội số của 128 bytes

      // Reset trạng thái upload
      this.#nextToSend = 0;
      this.#lastReceived = -1;
      this.#ControlPipe_rxQueue = [];
      this.#ControlPipe_busy = true;
      this.#PacketBufferSize = Uploader.#config.MaxPacketBufferSize;

      console.log('[START] Initializing upload......');
      for (let i = 0; i < Math.min(this.#PacketBufferSize, this.#packets.length); i++) {
        await this.#DataPipe_sendToLeanbot(this.#packets[i]);
        console.log(`Uploader: Sending packet #${i}`);
        this.#nextToSend++;
      }
    
      this.#ControlPipe_busy = false;
      this.#uploadEndMs = performance.now();

      // console.log("Waiting for Receive feedback...");
    }
    catch(e){
      console.log("Catch Upload Error:", e);
    }
  }

  #computePacketHashesHash32() {
    let hash32 = 0 >>> 0; // reset hash32
    for (let i = 0; i < this.#packets.length; i++) {
      hash32 = updateHashWithBytes(hash32, this.#packets[i]);
      this.#packetHashes[i] = hash32.toString(16).toUpperCase().padStart(8, '0');
    }
  }

  /** Setup Char + Notify + Queue */
  async setupConnection(characteristics, BLE_MaxLength, BLE_Interval, HASH) {
    this.#DataPipe_char    = characteristics[Uploader.#config.DataPipe_UUID] || null;
    this.#ControlPipe_char = characteristics[Uploader.#config.ControlPipe_UUID] || null;

    if (!this.isSupported()) {
      console.log("Uploader: Not supported");
      return;
    }

    if (!this.#ControlPipe_char.properties.notify) {
      console.log("Uploader Notify: Not supported");
      return;
    }

    await this.#ControlPipe_char.startNotifications();
    this.#ControlPipe_char.addEventListener("characteristicvaluechanged", (event) => {
      const BLEPacket = new TextDecoder().decode(event.target.value);
      this.#ControlPipe_onReceiveFromLeanbot(BLEPacket);
    });

    console.log("Callback Uploader.onMessage: Enabled");

    // Các lệnh thiết lập (nếu có)
    if (BLE_MaxLength) {
      const cmd = `SET BLE_MAX_LENGTH ${BLE_MaxLength}`;
      await this.#ControlPipe_sendToLeanbot(new TextEncoder().encode(cmd));
    }

    if (BLE_Interval) {
      const cmd = `SET BLE_INTERVAL ${BLE_Interval}`;
      await this.#ControlPipe_sendToLeanbot(new TextEncoder().encode(cmd));
    }
  }

  // ========== Queue handler ==========
  async #ControlPipe_rxQueueHandler() {
    if (this.#ControlPipe_busy) return;
    this.#ControlPipe_busy = true;

    while (this.#ControlPipe_rxQueue.length > 0) {
      const BLEPacket = this.#ControlPipe_rxQueue.shift();
      const LineMessages = BLEPacket.split(/\r?\n/).map(s => s.trim()).filter(Boolean);

      for (const LineMessage of LineMessages) {
        await this.onMessageInternal(LineMessage);
        this.onMessage(LineMessage);
      }
    }

    this.#ControlPipe_busy = false;
  };

  // ========== Message Processor ==========
  async onMessageInternal(LineMessage, onTransferInternal = true) {
    let m = null;

    // RSSI
    if (m = [...LineMessage.matchAll(/\[(-?\d+(?:\.\d+)?)\]/g)]) {
      // LineMessage = [2.897] [-54.3] Receive 56
      // m[0][0] = [2.897], m[1][0] = [-54.3]
      if (m.length >= 2){
        const rssi = m[1][1]; // rssi = -54.3
        this.onRSSI(rssi);
      }
    }

    // Transfer
    if (m = LineMessage.match(/Receive\s+(-?\d+)(?:\s+(\S+))?/i)) {
      const progress = parseInt(m[1]);
      const recvHash = m[2] ? m[2].toUpperCase() : null;
      if (recvHash) {
        const expected = this.#packetHashes[progress];
        console.log(`Expected Hash:`, expected);
        if (recvHash !== expected) {
          this.isUploadSessionActive = false;
          const err = `Transfer Error: Hash mismatch. ESP32: ${recvHash}, WEB: ${expected}`;
          await this.emitTransferError(err);
          return; // stop transfer
        }
      }
       
      this.isTransferring = true;
      if (progress === this.totalPackets) this.isTransferring = false;

      if (onTransferInternal) await this.#onTransferInternal(progress);
      this.onTransfer(progress + 1, this.totalPackets);
      return;
    }

    // Write
    if (m = LineMessage.match(/Write\s+(\d+)\s*bytes/i)) {
      const progress = parseInt(m[1]);
      this.onWrite(progress, this.totalBytesData);
      return;
    }

    // Verify
    if (m = LineMessage.match(/Verify\s+(\d+)\s*bytes/i)) {
      const progress = parseInt(m[1]);
      this.onVerify(progress, this.totalBytesData);
      return;
    }

    // Success
    if (/Upload success/i.test(LineMessage)) {
      this.isUploadSessionActive = false;
      await this.onSuccess();
      return;
    }

    // Errors
    if (/Write failed|Verify failed/i.test(LineMessage)) {
      this.isUploadSessionActive = false;
    }

    if (/Write failed/i.test(LineMessage)) {
      this.onWriteError();
      return;
    }

    if (/Verify failed/i.test(LineMessage)) {
      this.onVerifyError();
      return;
    }
  };

  // ========== Send next packet ==========
  #timeoutCount = 0;
  #timeoutTimer = null;
  #isSending = false;

  #clearTimeoutTimer() {
    if (this.#timeoutTimer) {
      clearInterval(this.#timeoutTimer);
      this.#timeoutTimer = null;
    }
    this.#timeoutCount = 0;
  }

  async #sendPacket(index) {
    if (index >= this.#packets.length) return;

    while (this.#isSending) await sleep(5);

    this.#isSending = true;
    await this.#DataPipe_sendToLeanbot(this.#packets[index]);
    this.#isSending = false;
  }

  async #startTimeoutForNextPacket() {
    this.#timeoutTimer = setInterval(async () => {
      this.#PacketBufferSize = 1;
      this.#nextToSend = this.#lastReceived + this.#PacketBufferSize;

      this.#timeoutCount++;
      console.log(`[TIMEOUT] TRIAL ${this.#timeoutCount}: Waiting for packet #${this.#nextToSend} response`);
      
      console.log(`[TIMEOUT] Uploader: Resending packet #${this.#nextToSend}`);
      await this.#sendPacket(this.#nextToSend);
      this.#nextToSend++;
      
      if (this.#timeoutCount >= 5) {
        this.#clearTimeoutTimer();
        const err = `Uploader: Transfer Error.`;
        this.isUploadSessionActive = false;
        await this.emitTransferError(err); 
      }
    }, Uploader.#config.timeoutDurationMs);
  }

  async #onTransferInternal(received) {
    console.log(`[RECV ${received}] onTransferInternal called`);

    if (received <= this.#lastReceived){
      console.log(`Uploader: Not the first time, ignore`);
      return;
    }

    this.#lastReceived = received;

    if (this.#PacketBufferSize < Uploader.#config.MaxPacketBufferSize) this.#PacketBufferSize++;

    const nextToSendLimit = received + this.#PacketBufferSize;
    while(this.#nextToSend <= nextToSendLimit && this.#nextToSend < this.#packets.length) {
      console.log(`Uploader: Sending packet #${this.#nextToSend}`);
      await this.#sendPacket(this.#nextToSend);
      this.#nextToSend++;
    }

    this.#clearTimeoutTimer();

    if (received + 1 >= this.#packets.length) {
      console.log(`Uploader: Leanbot received all #packets.`);
      return;
    }

    console.log(`Uploader: Setting timeout for packet #${received + 1}`);
    await this.#startTimeoutForNextPacket();
  }

  // ========== Control Pipe Communication ==========
  async #ControlPipe_sendToLeanbot(packet) {
    await this.#ControlPipe_char.writeValueWithoutResponse(packet);
  }

  async #ControlPipe_onReceiveFromLeanbot(packet){
    if (this.isUploadSessionActive !== true) return;

    this.#ControlPipe_rxQueue.push(packet);
    setTimeout(async () => await this.#ControlPipe_rxQueueHandler(), 0);
  }

  // ========== Data Pipe Communication ==========
  async #DataPipe_sendToLeanbot(packet) {
    try {
      await this.#DataPipe_char.writeValueWithoutResponse(packet);
    } catch (err) {
      this.isUploadSessionActive = false;
      await this.emitTransferError("Write Error:", err);
    }
  }

  cancel() {
    this.#ControlPipe_rxQueue = []; 
    this.#lastReceived = this.#packets.length;
    this.#ControlPipe_busy = true;
  }

  async emitTransferError(err) { // call both transfer error and general error
    console.log(err)
    this.onTransferError();
    await this.onError(err);
  }

  elapsedTimeMs() {
    if (!this.isSupported()) {
      if (this.#JDYUploader) return this.#JDYUploader.elapsedTimeMs();
    }

    if (this.#uploadEndMs === 0) // if upload not done yet
      return performance.now() - this.#uploadStartMs;
    return this.#uploadEndMs - this.#uploadStartMs;
  }

  /** Returns write-only BPS (JDY only); 0 if not available or not yet computed */
  getUploadWriteBps() {
    if (!this.isSupported()) {
      if (this.#JDYUploader) return this.#JDYUploader.getUploadWriteBps();
    }
    return 0; // not implement for lbEsp32 yet
  }

  getUploadStastics() {
    if (!this.isSupported()) {
      if (this.#JDYUploader) return this.#JDYUploader.getUploadStastics();
    }
    return {}; // not implement for lbEsp32 yet
  }

  getwriteMode(){
    if (!this.isSupported()) {
      if (this.#JDYUploader) return this.#JDYUploader.getwriteMode();
    }
    return 0; // not implement for lbEsp32 yet
  }

  EnterBootloaderTimeMs() {
    if (!this.isSupported()) {
      if (this.#JDYUploader) return this.#JDYUploader.EnterBootloaderTimeMs();
    }
    return 0; // not implement yet
  }

  getTotalTimeMs(){
    if (!this.isSupported()) {
      if (this.#JDYUploader) return this.#JDYUploader.getTotalTimeMs();
    }    
    return 0; // not implement for lbEsp32 yet
  }

  getwriteReadResponseRecords() {
    if (!this.isSupported()) {
      if (this.#JDYUploader) return this.#JDYUploader.getwriteReadResponseRecords();
    }    
    return ''; // not implement for lbEsp32 yet
  }

  abortAll(){
    this.isUploadSessionActive = false;
    this.isTransferring = false;

    if (!this.isSupported()) {
      if (this.#JDYUploader) this.#JDYUploader.abortAll();
      return;
    }

    this.#isSending = false;
    this.cancel();
  }

}

// ======================================================
// 🔹 SUBMODULE: JDYUploader
// ======================================================
class JDYUploader {

  static #config = null;

  static setConfig(config){
    JDYUploader.#config = config;
  }

  #leanbot;
  #serial;
  #uploader;

  isResetNano        = false;
  isUploading        = false;
  #isPreUpload       = true;

  #uploadAbortController = null;
  get #uploadSignal() { return this.#uploadAbortController?.signal; }

  #BLEPackets  = [];

  #uploadResponseBuffer = [];
  #expectedResponse = [];
  #lastExpectedIndex = -1; // -1 = getSync phase (ignore response); >= 0 = write/read phase (track response)
  // #isCommandPayloadSendCompleted = false;

  #writePageTimeStamp = []; // [{send half 1, send half 2, received 2 bytes, received 4 bytes}]
  // #writePageXStamp = {}; 

  #lastPageIndex    = -1; // use to log
  #verifyStepIndex  = -1; // sequential verify step (0-based); updated in verifyUploadedCode
  
  #enterBoothStartMs = 0;
  #enterBoothEndMs   = 0;

  #uploadStartMs = 0;
  #uploadEndMs = 0;

  #writeOnlyBps      = 0;  // write-only throughput in bytes/sec (excludes verify time)

  #CompileAndUploadStartMs = 0;
  #CompileAndUploadEndMs = 0;

  // #isFirstSync = false;
  #writeMode = 0;

  #isResponseOutOfSync  = false;
  #responseReSyncWatchdog = null;
  #writeReadResponseRecords = []; // [{ phase, page, errorType, responseInfo, isReSync }]
  #uploadStep = '';               // tracks current step for error reporting

  /* ------------------- CONSTRUCTOR ------------------- */
  constructor(ble, serial, uploader) {

    if (!JDYUploader.#config) {
      throw new Error("Missing JDYUploader #config");
    }

    this.#leanbot = ble;     // dùng được hàm của LeanbotBLE
    this.#serial  = serial;  // dùng được hàm của Serial
    this.#uploader = uploader; // dùng được hàm của Uploader

    this.#writePageTimeStamp = Array.from({ length: JDYUploader.#config.MaxPages }, () => ({
      sendHalf1: 0,
      sendHalf2: 0,
      recv2Bytes: 0,
      recv4Bytes: 0,
      startSend: 0
    }));

    // default writeMode = 8ms
    
    // const lastWriteMode = localStorage.getItem(JDYUploader.#config.LS_KEY_WRITE_MODE);
    // if(!lastWriteMode)
    //   this.#setwriteMode(JDYUploader.#config.writeFastMode); // first time (not yet have key in localStorage)
    // else
    //   this.#writeMode = lastWriteMode;

    // callback nhận dữ liệu từ Serial
    serial.onForwardReponse = async (bytes) => {
      await this.#handleResponse(bytes);
    };
  }



/* === #jdyEnterBootloader flow: ====
try{
  if leanbot.connected()
    isResetNano = true;     // Chỉ khi nào đang connected mới thực sự là ResetNano
    disconnect
    
    if disconnect failed
        throw err
    
    delay 3.5s  # minimum delay between disconnect and reconnect

  reconnect()
  
  if reconnect failed
    throw err

  isUploading = true;      // set Uploading = true để forward Serial pipe (serial char changed callback) => jdyuploader handler respond.

  sendAndWaitSync()        // 1 time

  onEnterBootloader()                       // callback for use in main.js (chưa sử dụng)
  
}
finally{
  isResetNano = false     // đảm bảo luôn được gọi khi có lỗi
}

// Không có retry ở trong hàm.
// upload2 sẽ gọi tối đa 3 lần jdyEnterBoothloader để retry.
*/

  async #jdyEnterBootloader(){

    this.isUploading = false;

    try{
      if(this.#leanbot.isConnected()){
        this.isResetNano = true;
        console.log("[JDY UPLOAD 2] Disconnecting...");
        const result = await this.#leanbot.disconnect();
        console.log("[JDY UPLOAD 2] Disconnect result:", result?.exception);
        if(!result.success)throw new Error(result.exception);
        await sleep(3500); // minimum delay between disconnect and reconnect
      }

      // multiple tries within X seconds
      console.log("[JDY UPLOAD 2] Reconnecting to enter bootloader...");
      const result = await this.#leanbot.reconnect();
      console.log("[JDY UPLOAD 2] Reconnect result:", result?.exception);

      if(!result.success)throw new Error(result.exception);
    }
    catch(e){ // Mark disconnect/reconnect errors as a separate error group => upload_cancel instead of error
      if (e instanceof Error) {
        e.type = "CONNECTION_ERROR";
      } else {
        e = Object.assign(new Error(String(e)), {
          type: "CONNECTION_ERROR",
          cause: e
        });
      }

      throw e; // Rethrow to let the caller handle the error
    }
    finally{
      this.isResetNano = false;
    }

    this.isUploading = true;

    console.log("[JDY UPLOAD 2] Wait for getSync to enter bootloader...");

    this.#enterBoothStartMs = performance.now();
    this.#enterBoothEndMs = 0;

    // wait until getSync Success (means entered bootloader)
    // this.#isFirstSync = true;
    const result = await this.#sendAndWaitSync();

    // Only read page 0 
    console.log(`[PAGE 0 VERIFY] Starting to read page 0...`);
    const bootloaderPage = await this.#readFlash_withoutCompare(0);
    // check if 
    if (bootloaderPage.length > 0 && bootloaderPage.every(byte => byte === 0x62)) {
      console.error('[PAGE 0 VERIFY] Invalid page: all data bytes are 0x62 => nano flash corrupted')
      throw Object.assign(new Error('Nano Kit corrupted'), {
        type: 'CORRUPTED_NANO'
      });
    }

    console.log(`[PAGE 0 VERIFY] Verify page 0 success. Ready to verify boothloader.`);

    // Only read first 2 page of bootloader (page 252, 253); do not compare.
    console.log(`[BOOTHLOADER VERIFY] Starting to read first 2 page of bootloader (page 252, 253)...`);
    const verifyBootloaderStart = 252;
    const verifyBootloaderEnd = verifyBootloaderStart + 2;
    let hash32 = 0 >>> 0; // reset hash32

    for (let page = verifyBootloaderStart; page < verifyBootloaderEnd; page++) {
      const bootloaderPage = await this.#readFlash_withoutCompare(page);
      hash32 = updateHashWithBytes(hash32, bootloaderPage); // update hash bootloader page
    }

    console.log(`[BOOTHLOADER VERIFY] All required pages read successfully. Ready to write.`);
    const hashString = numberToHex(hash32)
    console.log(`[BOOTLOADER VERIFY] Final hash32:${hashString}`);
  
    this.#enterBoothEndMs = performance.now();

    console.log("[JDY UPLOAD 2] Entered bootloader successfully!");
    await this.#uploader.onEnterBootloader(result.retry, hashString);  // callback for use in main.js

  }

/* === jdyUpload2 flow: ====
try{
    Try max 3 times to Enter Bootloader
      try #jdyEnterBootloader()                    // wrap trong try-catch, nếu failed (throw err) < 3 lần thì được thử lại.
      
      if failed 3 times                      // failed 3 lần jdyEnterBoothloader => throw err để ngừng upload.
        throw err

    every 250ms until compilePromise          // to keep Nano stay in bootloader until hex file is ready
      sendAndWaitSync()
 
    get HexText from compilePromise           // sau khi compile thu được hex text (dạng string)
    
    convert HexText => HexPacket              // chia hexText => các gói 128 byte.
    
    upload HexFile                            // bắt đầu upload

}catch{  }                                     // bất cứ throw nào => dừng uplaod ngay.

finally { abortAll}                            // xóa các cờ nội bộ (hoặc gọi abort Signal) để reset jdyUpload về trạng thái mặc định.
*/

  async jdyUpload2(compilePromise){

    this.#CompileAndUploadStartMs = performance.now();
    this.#CompileAndUploadEndMs = 0;
    this.#expectedResponse = [];
    this.#lastExpectedIndex = -1; // start in getSync phase; will be set to 0 before write loop
    this.#uploadAbortController = new AbortController();
    this.#isResponseOutOfSync  = false;
    this.#responseReSyncWatchdog = null;
    this.#writeReadResponseRecords = [];
    this.#uploadStep = '';
    this.#serial.clearUploadWriteRecords();

    let compileLog = null;

    try{
      this.#isPreUpload = true;

      if(this.#leanbot.BluetoothApiNotSupported ||
          this.#leanbot.SecureContextMissing ||
          this.#leanbot.BluetoothAdapterNotAvailable
      ){
        await this.#uploader.onCancelUpload('Bluetooth is not available or not supported');
        return { success: false, exception: 'cancelled'};
      }
      
      // Try max 3 times to Enter Bootloader (not implement retry mechanism yet)
      const MAX_ATTEMPTS = 3;
      let attempt = 0;

      for (; attempt < MAX_ATTEMPTS; attempt++) {
        this.#uploadStep = `enter bootloader attempt: ${attempt}`;
        try {
          await this.#jdyEnterBootloader();
          console.log(`[JDY UPLOAD 2] Enter bootloader success on attempt ${attempt + 1}, now in bootloader mode.`);
          break; // break if success
        }
        catch (err) {
          console.log(`[JDY UPLOAD 2] Enter bootloader attempt ${attempt + 1} catch:`, err);
          const message = err?.message || err;
          
          // CONNECTION_ERROR => upload cancel instead of retry
          if (err instanceof Error && err.type === "CONNECTION_ERROR") {
            const m = message.match(
              /(gatt\.connect\(\)|server\.GetPrimaryService\(\))\s+(timeout after \d+\s*ms|exception:\s*[\s\S]+)$/i
            );

            let noidung = message;
            let thongtin = null;

            if (m) { // gatt.connect()/server.GetPrimaryService() error
              // m[1] = gatt.connect() | server.GetPrimaryService()
              // m[2] = timeout after 6000 ms | exception: <original error>

              noidung = message
                .replace(m[1], "")
                .replace(/exception:\s*/i, "")
                .trim();
              thongtin = m[0];
            }

            await this.#uploader.onCancelUpload(noidung, thongtin);
            return { success: false, exception: 'cancelled' };
          }

          // CORRUPTED NANO ERROR => stop
          if (err instanceof Error && err.type === "CORRUPTED_NANO") {
            throw err;
          }

          // if(message === 'cancelled'){ // cancel => stop entirely
          //   await this.#uploader.onCancelUpload('Request cancelled');
          //   return { success: false, exception: 'cancelled'};
          // }

          // if (/Connection attempt|Reconnect timeout/i.test(message)){
          //   throw new Error(`gatt.connect() failed: ${message}`); // must be gatt.connect() failed or reconnect attempt timeout => stop try enter bootloader.
          // }

          // if (/GATT Server is disconnected/i.test(message)){
          //   throw new Error(`server.GetPrimaryService() failed: ${message}`);
          // }


          // attempt to sync failed
          if (attempt === MAX_ATTEMPTS - 1) {
            throw new Error(`Enter bootloader failed: ${message}`);
          }
        }
      }

      // Try send then wait for sync ack until every 500ms until compilePromise done
      console.log("[JDY UPLOAD 2] periodly send and wait Sync to keep Nano stay in bootloader until hex file is ready...");

      // const HexText = await this.#waitCompilingWhileSendSync(compilePromise);
      this.#uploadStep = 'get sync (wait compiling complete)';
      const compileResult = await this.#waitCompilingWhileSendSync(compilePromise);

      // if (HexText  === null) {
      if (!compileResult.success) {
        await this.#uploader.onCancelUpload('Compile failed');
        return { success: false, exception: 'cancelled'};
      }

      compileLog = compileResult.log;

      this.#BLEPackets = convertHexToBlePackets(compileResult.hexText, { returnStep2: true });

      console.log("[JDY UPLOAD 2] Hex file is ready, now start upload.");

      this.#uploadStartMs = performance.now();
      this.#uploadEndMs   = 0;
      this.#writeOnlyBps  = 0;  // reset at upload start to avoid stale value from a previous run
      this.#isPreUpload   = false;

      // upload HexFile
      await this.#uploadCode();
      
      console.log("[JDY UPLOAD 2] Upload code done.");
      return { success: true, exception: null };
    }
    catch (err) {
      let message = err?.message ?? String(err);
      console.log("[JDY UPLOAD 2] Upload error:", message);

      // Emit write/verify failed message based on error phase
      if (/^Write page/i.test(message)) {
        this.#emitUploadMessage("Write failed");
      } else if (/^Read page/i.test(message)) {
        this.#emitUploadMessage("Verify failed");
      }

      let thongtin = null;
      const osType = navigator.userAgentData?.platform || navigator.platform || 'Unknown';

      // // enter bootloader exception.
      // let m = message.match(/^(Enter bootloader failed|gatt\.connect\(\) failed|server\.GetPrimaryService\(\) failed):\s*(.+)$/is);
      // if (m) {
      //   // "Enter bootloader failed: Reconnect failed: ..."
      //   // "gatt.connect() failed: ..."
      //   // "server.GetPrimaryService() failed: ..."

      //   // m[1] = loại lỗi
      //   // m[2] = phần còn lại sau ":"

      //   thongtin = m[2] ?? '';
      //   message = m[1];

      //   if (message.includes('gatt.connect()')) {
      //     const match = thongtin.match(/timeout after \d+ ms/i);
      //     message = `gatt.connect() ${match ? match[0] : `exception: ${thongtin}`}`;

      //     // Chỉ GATT error mới cancel + return
      //     await this.#uploader.onCancelUpload(message, thongtin);
      //     return { success: false, exception: 'cancelled' };
      //   }
      //   else if (message.includes('server.GetPrimaryService()')) {
      //     message = `server.GetPrimaryService() exception: ${thongtin}`;

      //     // Chỉ GATT error mới cancel + return
      //     await this.#uploader.onCancelUpload(message, thongtin);
      //     return { success: false, exception: 'cancelled' };
      //   }
      // } else 
      
      if (/^(Write|Read) page/i.test(message)) { // upload procure exception
        message = message.replace(/^.*\b(Write|Read) page\s+\S+\s+/i, '$1 page '); // bỏ prefix + page number
        message = message.replace(/\s+after\s+\d+\S*.*$/i, '');                    // bỏ timing suffix

        // Write/Read/Verify error => format giống upload_done (bỏ speed)
        const mismatchRecords = this.getwriteReadResponseRecords() || '';

        const speed = this.getUploadWriteBps();

        const match = compileLog?.match(/Sketch uses\s+(\d+)\s+bytes.*?Maximum is\s+(\d+)\s+bytes/i);
        const used = Number(match?.[1] ?? 0);
        const max = Number(match?.[2] ?? 0);
        const FlashUsage = ((used / max) * 100).toFixed(2);

        thongtin =
            `${FlashUsage}% [${osType}]\n` +
            (mismatchRecords ? mismatchRecords : '');

      } else if (/(Get Sync|Invalid |Mismatch)/i.test(message)) {
        // Fallback: parse page generically if present in message, keep original format
        // thongtin = `[page = ${this.#lastPageIndex}] receive[...] = [...]`;
        const pageNum    = message.match(/page (\d+)/i)?.[1] ?? '?';
        const receiveHex = bytesToHex(this.#uploadResponseBuffer);
        thongtin = `receive[${this.#uploadResponseBuffer.length}] = [${receiveHex}]`;

        //   this.#setwriteMode(JDYUploader.#config.writeSafeMode); // if mismatch => switch to safe mode
      } else if(message.toLowerCase().includes('gatt')){
        // Generic error (GATT, BLE disconnect, abort, etc.) — include step info
        const logMatch   = compileLog?.match(/Sketch uses\s+(\d+)\s+bytes.*?Maximum is\s+(\d+)\s+bytes/i);
        const used       = Number(logMatch?.[1] ?? 0);
        const max        = Number(logMatch?.[2] ?? 1);
        const flashUsage = max > 0 ? ((used / max) * 100).toFixed(2) : 0;
        thongtin = `${flashUsage}% [${osType}]\n${this.#uploadStep || 'unknown step'}\n${this.#serial.getUploadWriteRecords()}`;
      }
      await this.#uploader.onError(message, thongtin);
      return { success: false, exception: message };
    }
    finally{
      this.#uploadEndMs = performance.now();
      this.#CompileAndUploadEndMs = this.#uploadEndMs;
      this.abortAll("Clear upload state"); // clear all flags and intervals
    }
  }

  /* Helper funcions (for jdy upload 2 only) */
  async #sendAndWaitSync(){

    // send getSync every 100ms
    this.#uploadResponseBuffer = [];

    const sendAndCheckGetSyncOK = async (retry) => {  

      if(this.#uploadSignal?.aborted){
        const reason = this.#uploadSignal.reason;
        const reasonMsg = reason instanceof Error ? reason.message : String(reason ?? 'unknown');
        throw new Error(`Get Sync ACK wait aborted: ${reasonMsg}`);
      }

      const isGetSyncOK = this.#checkGettingSyncDone() === true; // throw if received invalid ack

      if(!isGetSyncOK)await this.#sendGetSync()

      return isGetSyncOK;
    }

    // wait until receive Sync ACK (or timeout)
    return await retryUntilTimeout(sendAndCheckGetSyncOK, "Get Sync ACK", JDYUploader.#config.getSyncTimeoutMs, JDYUploader.#config.getSyncRepeatMs);
  }

  async #waitCompilingWhileSendSync(compilePromise) {

    // Abort ngay lập tức khi abortAll() được gọi (không cần đợi hết syncIntervalMs)
    const abortPromise = new Promise((_, reject) => {
      this.#uploadSignal?.addEventListener('abort', () =>
        reject(new Error('Upload aborted, reason: ', this.#uploadSignal.reason))
      );
    });

    while (true) {

      const result = await Promise.race([
        compilePromise,
        sleep(JDYUploader.#config.syncIntervalMs),
        abortPromise,   // resolve ngay khi signal.abort() được gọi
      ]);
    
      if ( result !== undefined ) {   // undefined if timeout after SYNC_INTERVAL
        // return result.hexText;
        return result;
      }
     
      // sendAndWaitSync() here
      await this.#sendAndWaitSync();
    }
  }

  /* ------------------- HANDLE RESPONSE ------------------- */
  async #handleResponse(bytes) { // callback inside onCharacteristicChanged.
    try{
      const receiveHex = bytesToHex(bytes);
      console.log("[UPLOAD] Received response: ", receiveHex);
      this.#uploadResponseBuffer.push(...bytes);

      if(this.#lastExpectedIndex < 0)return; // getSync phase: ignore response (not tracked via expectedResponse[])

      const expectedIncomingLength = this.#expectedResponse.length - this.#lastExpectedIndex;

      // unexpected data coming => not throw, just warning (may be just stray bytes from last command)
      if(expectedIncomingLength <= 0){
        console.warn("[UPLOAD] Unexpected Response received!");
      }

      // // incoming response already larger than exptect => strip last expect bytes 
      // // (may be an stray bytes from last command attach to current response) => last bytes must be the current response
      // if(bytes.length > expectedIncomingLength){
      //   bytes = bytes.slice(bytes.length - expectedIncomingLength);
      //   console.warn(`[UPLOAD] Received response larger than expected. Strip to: `, bytesToHex(bytes));
      // }
      const compareLength = Math.min(bytes.length, expectedIncomingLength);

      const ReceivedTimestamp = performance.now();

      // compare received bytes with expected response.
      // for (let i = 0; i < bytes.length; i++) {
      for (let i = 0; i < compareLength; i++) {

        const isWritePage  = this.#lastExpectedIndex < this.#uploader.totalPackets * 4;
        const expectedByte = this.#expectedResponse[this.#lastExpectedIndex];
        const receivedByte = bytes[i];

        if(expectedByte !== receivedByte){ // not match
          
          // this.#setwriteMode(JDYUploader.#config.writeSafeMode); // if mismatch => switch to safe mode
          const _phase = isWritePage? "Write": "Read";
          console.log(`[UPLOAD] [${_phase}] Response byte mismatch at index ${this.#lastExpectedIndex}: expected 0x${expectedByte?.toString(16)}, received 0x${receivedByte.toString(16)}`);

          
          const pages = this.#buildPagesFromBlocks(this.#BLEPackets, JDYUploader.#config.pageSizeByte);
          const pageData = pages[this.#lastPageIndex]?.bytes;

          // Reconstruct full write payload (giống writeFlash3)
          const byteAddress = this.#lastPageIndex * JDYUploader.#config.pageSizeByte;
          const wordAddress = byteAddress >> 1;
          const fullPayload = new Uint8Array([
            0x55, wordAddress & 0xFF, (wordAddress >> 8) & 0xFF, 0x20,
            0x64, 0x00, JDYUploader.#config.pageSizeByte, 0x46,
            ...pageData,
            0x20
          ]);

          // Tìm bytes trong full payload
          let matchOffset = bytes.length > 0 ? fullPayload.indexOf(bytes[0]) : -1;
          if (matchOffset >= 0) {
            const compareLen = Math.min(bytes.length, fullPayload.length - matchOffset);
            for (let j = 0; j < compareLen; j++) {
              if (fullPayload[matchOffset + j] !== bytes[j]) { 
                matchOffset = -1; 
                break; 
              }
            }
          }

          // Xác định first/last half dựa vào mode + chiều dài response
          const isFirstBigger = this.getwriteMode() === JDYUploader.#config.writeFastMode;
          const isBiggerPiece = bytes.length > fullPayload.length - bytes.length;
          const halfLabel = (isFirstBigger === isBiggerPiece) ? 'first' : 'last';

          // log
          console.log(
            "Mismatch info: " 
            + (matchOffset >= 0
              ? ` matches ${halfLabel} write payload (byte ${matchOffset} -> ${matchOffset + bytes.length - 1} in page ${this.#lastPageIndex})`
              : ` does not match write payload`)
            + `\n[${bytes.length} bytes] ${bytesToHex(bytes)}\n`
          );

          // throw new Error(`${_phase} page ${this.#lastPageIndex} response not matched`);
          const _errMsg =  _phase === 'Read'
            ? `Read page ${this.#lastPageIndex}.${this.#verifyStepIndex} mismatched`
            : `Write page ${Math.floor(this.#lastExpectedIndex / 4)} mismatched`;
          
          if (!this.#isResponseOutOfSync) { // only record first mismatch
            this.#addToResponseRecords(_errMsg);
          }

          // if it is write mismtach => not throw, instead let response have a chance to re-sync 
          // if(isWritePage){
          this.#isResponseOutOfSync = true;
          // remove the current response (as they are mismatch)
          this.#uploadResponseBuffer.splice(this.#uploadResponseBuffer.length - bytes.length);
          return; // need to stop the current response anyway (without update the expected index).
          // }

          // if read mismatch => throw erro immediately
          // throw new Error(_errMsg); // throw => stop upload immediately via abort signal.
        }

        // matched
        if(this.#responseReSyncWatchdog) // response matched when out of sync => stil in sync
        {
          console.log("[ResponseSync] Received valid Response < 500ms => response still in Sync");
          this.#responseReSyncWatchdog.resolve();
          // this.#uploadResponseBuffer.slice(this.#uploadResponseBuffer.length - bytes.length);
          return; // not update expected index => when resume: try read/write this same page again
        }
          
        if(isWritePage){ // is write page => mark timestamp
          const pageIndex = Math.floor(this.#lastExpectedIndex / 4);
          if(this.#lastExpectedIndex % 4 === 1)
            this.#writePageTimeStamp[pageIndex].received2 = ReceivedTimestamp - this.#writePageTimeStamp[pageIndex].startSend;
          if(this.#lastExpectedIndex % 4 === 3){
            this.#writePageTimeStamp[pageIndex].received4 = ReceivedTimestamp - this.#writePageTimeStamp[pageIndex].startSend;
            console.log(`[WRITE] PROG_PAGE ACK received! (page ${pageIndex})`);
          }
        }

        this.#lastExpectedIndex += 1; // advance after bytes have matched
      }
    }
    catch(e){
      console.log("[UPLOAD] Error handling response", e);
      this.abortAll(e);
    }
  }

  #isResponseMatchExpected(){
    if(this.#lastExpectedIndex > this.#expectedResponse.length){
      throw new Error("Abnormal: lastExpectedIndex should never be larger than expectedResponse length");
    }
    return this.#lastExpectedIndex === this.#expectedResponse.length;
  }

  #addToResponseRecords(err){
    console.log('[UPLOAD] Detected: ', err);

    // Format-based: strip timing suffix, then split by position (no hardcoded keywords)
    const core  = err.replace(/\s+after\s+\d+\S*.*/i, ''); // strip "after N ms"
    const match = core.match(/(Write|Read) page (\S+)\s*(.*)/i);
    const phase     = match?.[1] ?? 'Unknown';             // 'Write' | 'Read'
    const page      = match?.[2] ?? 'unknown';                // '15', '47.0'
    const errorType = match?.[3]?.trim() || 'unknown';

    const responseInfo = `[${this.#uploadResponseBuffer.length}] = ${shortenArray(this.#uploadResponseBuffer)}`;

    this.#writeReadResponseRecords.unshift({ phase, page, errorType, responseInfo, isReSync: false });
  }

  async #waitUntilResponseResync(){ // wait to resync from the last response records.
    try{
      console.log('[ResponseSync] mismatch detected, pausing read/write flow');

      this.#responseReSyncWatchdog = (() => {
        let resolveInternal;
        const promise = new Promise((resolve) => {
          resolveInternal = resolve;
        });
            
        return {
          promise,
          resolve() {
            resolveInternal(true);
          },
        };
      })();

      console.log('[ResponseSync] Wait Response within 500ms')
      // wait if response will come in
      const isResponseStillInSync = await Promise.race([
        this.#responseReSyncWatchdog.promise,
        sleep(500),
      ])

      // Get latest record (newest-first) => build error message directly from fields.
      const latest = this.#writeReadResponseRecords[0];
      const errorMessage = `${latest.phase} page ${latest.page} ${latest.errorType}`;
      
      const currentIndex = this.#lastExpectedIndex;
      if(isResponseStillInSync !== true){ // not in sync (wait timeout) => try resync only one time.
        console.log("[ResponseSync] no Response within 500ms => try send getSync and wait for ACK one time")
        
        // Tag isReSync trên record mới nhất (đầu mảng)
        if (this.#writeReadResponseRecords[0]) {
          this.#writeReadResponseRecords[0].isReSync = true;
        }

        this.#lastExpectedIndex = -1; // reset

        let isSendCompleted = false;
        const sendPayloadPromise = async () => {
          await this.#sendGetSync();
          isSendCompleted = true;
        }

        const waitResponsePromise =  this.#waitUntil(() => (isSendCompleted && this.#checkGettingSyncDone()) , 
          JDYUploader.#config.getSyncTimeoutMs, 
          () => isSendCompleted
            ? `Try Resync waitResponse`
            : `Try Resync sendPayload`
        ); // safe-wait read page

        // try {
        //   await this.#waitUntil(() => this.#checkGettingSyncDone(), JDYUploader.#config.getSyncTimeoutMs, "Try Resync"); // throw if abort/timeout
        // }
        // catch(e){
        //   console.log("[ResponseSync] resync Failed => Abort, reason: ", e);
        //   throw new Error(errorMessage); // throw => terminate upload at once (without current response)
        // }
            
        try {
          await Promise.all([
            sendPayloadPromise(), 
            waitResponsePromise,
          ]);       
        }
        catch(e){
          console.log("[ResponseSync] resync Failed => Abort, reason: ", e);
          throw new Error(errorMessage); // throw => terminate upload at once (without current response)
        }

      }
      // re-sync success (or still inSync from the begining)
      this.#lastExpectedIndex = currentIndex;
      console.log('[ResponseSync] resync completed, resuming read/write flow');
    }
    finally{
      this.#isResponseOutOfSync  = false;
      this.#responseReSyncWatchdog = null;
    }

  }
  

  /*Safe wait loop*/

  async #waitUntil(conditionFlag = () => {}, timeoutMs, label) {
    const start = performance.now();
    while (!conditionFlag()) {

      const resolvedLabel = typeof label === 'function' ? label() : label;

      if(this.#uploadSignal?.aborted){
        const reason = this.#uploadSignal.reason;
        const reasonMsg = reason instanceof Error ? reason.message : String(reason ?? 'unknown');
        throw new Error(`${resolvedLabel} aborted: ${reasonMsg}`); // upload depend on its to be able to stop
      }

      if (performance.now() - start > timeoutMs) {
        // this.abortAll(`${label} timeout after ${timeoutMs} ms`); // made sure it can still terminate upload even if the promise become orphaned.
        throw new Error(`${resolvedLabel} timeout after ${timeoutMs} ms`);
      }
      await sleep(2);
    }
    return true; // condition met; callers can distinguish this from sleep() which resolves undefined
  }

  /* ------------------- GET SYNC ------------------- */
  async #sendGetSync() {
    // STK_GET_SYNC + STK_CRC_EOP
    const getSyncCmd = new Uint8Array([0x30, 0x20]); 
    console.log("[SYNC] Sent GET_SYNC command");
    this.#uploadResponseBuffer = [];
    await this.#serial.SerialPipe_sendToLeanbot(getSyncCmd, true); // send with response
  }

  
  #checkGettingSyncDone() {

    if(this.#uploadResponseBuffer.length < 2)return false; // at least sync-ok

    // the last byte MUST be OK for a valid ACK response
    if(!this.#isResponseOK(this.#uploadResponseBuffer[this.#uploadResponseBuffer.length - 1]))return false;
    // possible ...*(sync-ok)
    // check only the last 2 bytes to see if it contains a valid ACK pair (sync-ok)
    if(!this.#isACK(this.#uploadResponseBuffer, this.#uploadResponseBuffer.length - 2, 2)){
      console.log("[SYNC] Invalid SYNC ACK response");
      this.#emitUploadMessage("Get Sync failed");
      throw new Error('Invalid SYNC ACK response');
    }

    console.log("[SYNC] SYNC ACK received!");
    // if(this.#isFirstSync){
    //   this.#enterBoothStartMs = performance.now();
    //   this.#enterBoothEndMs = 0;
    //   this.#isFirstSync = false;
    // }
    return true;
  }

  /* ------------------- READ FLASH ------------------- */
  async #readFlash2(pageIndex = 0, pageData) {
    this.#uploadStep = `read page ${pageIndex}`;
    this.#lastPageIndex = pageIndex;
    console.log(`[READ] Page ${pageIndex}`);

    // 1) load address
    const byteAddress = pageIndex * JDYUploader.#config.pageSizeByte;
    const wordAddress = byteAddress >> 1;
    const addrLow     = wordAddress & 0xFF;
    const addrHigh    = (wordAddress >> 8) & 0xFF;

    // STK_LOAD_ADDRESS + addrLow + addrHigh + STK_CRC_EOP
    const loadAddressCmd = new Uint8Array([0x55, addrLow, addrHigh, 0x20]);

    // 2) read page: STK_READ_PAGE + len_hi + len_lo + 'F' + STK_CRC_EOP
    const readPageCmd = new Uint8Array([0x74, 0x00, JDYUploader.#config.pageSizeByte, 0x46, 0x20]);
    console.log("[READ] Sent READ_PAGE command");
    this.#uploadResponseBuffer = []; // clear up earlier data (if any)

    const readPagePayload = new Uint8Array([...loadAddressCmd, ...readPageCmd]);

    const readPageResponse = new Uint8Array([0x14, 0x10, 0x14, ...pageData, 0x10]); // sync - ok - sync - data[128] - ok (formal response for read page command)
    this.#expectedResponse.push(...readPageResponse);

    let isSendCompleted = false;

    const sendPayloadPromise = async () => {
      await this.#serial.SerialPipe_sendToLeanbot(readPagePayload, true);
      isSendCompleted = true;
    }

    // await this.#waitUntil(() => this.#checkReadingPageDone(), JDYUploader.#config.readFlashTimeoutMs, `Read page ${pageIndex} ack`); // safe-wait read page
    // await this.#waitUntil(() => this.#isResponseMatchExpected(), JDYUploader.#config.readFlashTimeoutMs, `Read page ${pageIndex} ack`); // safe-wait read page

    const waitResponsePromise =  this.#waitUntil(() => (isSendCompleted && this.#isResponseMatchExpected())  || this.#isResponseOutOfSync, 
      JDYUploader.#config.readFlashTimeoutMs, 
      () => isSendCompleted
        ? `Read page ${pageIndex}.${this.#verifyStepIndex} wait response`
        : `Read page ${pageIndex}.${this.#verifyStepIndex} send payload`
    ); // safe-wait read page

    // try{
    //   await this.#waitUntil(() => this.#isResponseMatchExpected(), JDYUploader.#config.readFlashTimeoutMs, `Read page ${pageIndex} ack`); // safe-wait read page
    // }
    // catch(e){
    //   if (e.message?.includes('timeout')) { // add to records and then throw err
    //     const message = `Read page ${pageIndex}.${this.#verifyStepIndex} timeout`;
    //     this.#addToResponseRecords(message);
    //     throw message;
    //   }    
    //   throw e; // rethrow 
    // }

    try{
      await Promise.all([
        sendPayloadPromise(), 
        waitResponsePromise,
      ]);
    }
    catch(e){
      if (e.message?.includes('timeout')) { // add to records and then throw err (only when waitResponse timeout)
        this.#addToResponseRecords(e.message);
      }    
      throw e; // rethrow 
    }

  }

  async #readFlash_withoutCompare(pageIndex = 0) {
    this.#lastPageIndex = pageIndex;
    console.log(`[READ WITHOUT COMPARE] Page ${pageIndex}`);

    // 1) load address
    const byteAddress = pageIndex * JDYUploader.#config.pageSizeByte;
    const wordAddress = byteAddress >> 1;
    const addrLow     = wordAddress & 0xFF;
    const addrHigh    = (wordAddress >> 8) & 0xFF;

    // STK_LOAD_ADDRESS + addrLow + addrHigh + STK_CRC_EOP
    const loadAddressCmd = new Uint8Array([0x55, addrLow, addrHigh, 0x20]);

    // 2) read page: STK_READ_PAGE + len_hi + len_lo + 'F' + STK_CRC_EOP
    const readPageCmd = new Uint8Array([0x74, 0x00, JDYUploader.#config.pageSizeByte, 0x46, 0x20]);
    console.log("[READ WITHOUT COMPARE] Sent READ_PAGE command");
    this.#uploadResponseBuffer = []; // clear up earlier data (if any)

    const readPagePayload = new Uint8Array([...loadAddressCmd, ...readPageCmd]);

    await this.#serial.SerialPipe_sendToLeanbot(readPagePayload, true);
    const readResult = await this.#waitUntil(() => this.#checkReadingPageDone(), JDYUploader.#config.readFlashTimeoutMs, `Read page ${pageIndex} ack`); // safe-wait read page
  
    if (!readResult) {
      console.error(`[READ WITHOUT COMPARE] Failed to read page ${pageIndex}`);
      throw new Error(`Failed to read page ${pageIndex}`);
    }

    console.log(`[READ WITHOUT COMPARE] Read page ${pageIndex} successfully`);

    return this.#stripReadingPageFrame(); // strip sync-ok overhead from read page response
  }

  #checkReadingPageDone() {
    const response = this.#uploadResponseBuffer;
    const minLength = JDYUploader.#config.pageSizeByte + 4;

    // Not enough data received yet
    if (response.length < minLength) return false;

    const lastIndex = response.length - 1;

    // Check the frame backward: OK ← DATA ← SYNC ← OK ← SYNC
    if (!this.#isResponseOK(response[lastIndex])) return false;
    if (!this.#isResponseSync(response[lastIndex - minLength + 1])) return false;

    return true;
  }

  #stripReadingPageFrame() {
    const response = this.#uploadResponseBuffer;
    const dataLength = JDYUploader.#config.pageSizeByte;
    const endIndex = response.length - 1;

    // Strip the trailing OK and the three header bytes by extracting DATA backward
    return Uint8Array.from(
      response.slice(endIndex - dataLength, endIndex)
    );
  }

  // /* Đọc 1 page và chờ đến khi nhận xong */
  // async #readPageAndGetData(pageIndex) {
  //   await this.#readFlash2(pageIndex);
  //   return this.#uploadResponseBuffer;
  // }

  /* ------------------- WRITE FLASH ------------------- */
  // async #writeFlash2(pageIndex, pageData) {
  //   this.#lastPageIndex = pageIndex;

  //   console.log(`[WRITE] Page ${pageIndex}`);

  //   if (!(pageData instanceof Uint8Array) || pageData.length !== JDYUploader.#config.pageSizeByte) {
  //     throw new Error(`pageData must be Uint8Array[${JDYUploader.#config.pageSizeByte}]`);
  //   }

  //   // 1) load address
  //   const byteAddress = pageIndex * JDYUploader.#config.pageSizeByte;
  //   const wordAddress = byteAddress >> 1;
  //   const addrLow     = wordAddress & 0xFF;
  //   const addrHigh    = (wordAddress >> 8) & 0xFF;

  //   // STK_LOAD_ADDRESS + addrLow + addrHigh + STK_CRC_EOP
  //   const loadAddressCmd = new Uint8Array([0x55, addrLow, addrHigh, 0x20]);

  //   // 2) PROG PAGE
  //   const progPageHeader = new Uint8Array([0x64, 0x00, JDYUploader.#config.pageSizeByte,  0x46]); // STK_PROG_PAGE + len_hi + len_lo + 'F'
  //   const progPageTail = new Uint8Array([0x20]); // STK_CRC_EOP

  //   const progPagePayload = new Uint8Array([...loadAddressCmd, ...progPageHeader, ...pageData, ...progPageTail]);

  //   // this.#writePageXStamp = {}; // reset for this page
  //   this.#writePageTimeStamp[pageIndex].startSend = performance.now();
  //   console.log("[WRITE] Sending PAGE command");

  //   // this.#isCommandPayloadSendCompleted = false;
  //   this.#uploadResponseBuffer = [];

  //   const ProgPageResponse = new Uint8Array([0x14, 0x10, 0x14, 0x10]); // sync - ok - sync - ok 
  //   this.#expectedResponse.push(...ProgPageResponse);

  //   // const half = Math.ceil(progPagePayload.length / 2);
  //   // const half = 9; // (half 1: 9 bytes, half 2: 128 bytes)
  //   const half = 128; // (half 1: 128 bytes, half 2: 9 bytes)

  //   await sleep(0); // yield
  //   await this.#serial.SerialPipe_sendToLeanbot( // send 128 bytes
  //     progPagePayload.subarray(0, half),
  //     false
  //   );
  //   this.#writePageTimeStamp[pageIndex].sendH1 = performance.now() - this.#writePageTimeStamp[pageIndex].startSend;
  //   console.log("Send Half 1 Done");
  //   await sleep(0); // yield
  //   await this.#serial.SerialPipe_sendToLeanbot( // send 9 bytes
  //     progPagePayload.subarray(half),
  //     true
  //   );
  //   this.#writePageTimeStamp[pageIndex].sendH2 = performance.now() - this.#writePageTimeStamp[pageIndex].startSend;
  //   console.log("Send Half 2 Done");
  //   // this.#isCommandPayloadSendCompleted = true;

  //   const shouldUpdate = this.#shouldUpdateProgress(pageIndex);

  //   if(shouldUpdate){
  //     this.#emitUploadMessage(`Receive ${pageIndex + 1}`);
  //   }

  //   //rest until received all ack
  //   // await this.#waitUntil(() => this.#checkWritingPageDone(), JDYUploader.#config.writeFlashTimeoutMs, `Write page ${pageIndex} ack`);
  //   try{
  //     await this.#waitUntil(() => this.#isResponseMatchExpected(), JDYUploader.#config.writeFlashTimeoutMs, `Write page ${pageIndex} ack`);
  //   }
  //   catch(e){
  //     console.log(`[WRITE] Write Page ${pageIndex} failed!`);
  //     this.#emitUploadMessage("Write failed");
  //     throw e;
  //   }

  //   if(shouldUpdate){
  //     this.#emitUploadMessage(`Write ${(pageIndex + 1) * JDYUploader.#config.pageSizeByte} bytes`);
  //     await sleep(0); // yield
  //   }
  // }
 
  async #writeFlash3(pageIndex, pageData) {
    this.#uploadStep = `write page ${pageIndex}`;
    this.#lastPageIndex = pageIndex;

    console.log(`[WRITE] Page ${pageIndex}`);

    if (!(pageData instanceof Uint8Array) || pageData.length !== JDYUploader.#config.pageSizeByte) {
      throw new Error(`pageData must be Uint8Array[${JDYUploader.#config.pageSizeByte}]`);
    }

    // 1) load address
    const byteAddress = pageIndex * JDYUploader.#config.pageSizeByte;
    const wordAddress = byteAddress >> 1;
    const addrLow     = wordAddress & 0xFF;
    const addrHigh    = (wordAddress >> 8) & 0xFF;

    // STK_LOAD_ADDRESS + addrLow + addrHigh + STK_CRC_EOP
    const loadAddressCmd = new Uint8Array([0x55, addrLow, addrHigh, 0x20]);

    // 2) PROG PAGE
    const progPageHeader = new Uint8Array([0x64, 0x00, JDYUploader.#config.pageSizeByte,  0x46]); // STK_PROG_PAGE + len_hi + len_lo + 'F'
    const progPageTail = new Uint8Array([0x20]); // STK_CRC_EOP

    const progPagePayload = new Uint8Array([...loadAddressCmd, ...progPageHeader, ...pageData, ...progPageTail]);

    // this.#writePageXStamp = {}; // reset for this page
    this.#writePageTimeStamp[pageIndex].startSend = performance.now();
    console.log("[WRITE] Sending PAGE command");

    // this.#isCommandPayloadSendCompleted = false;
    this.#uploadResponseBuffer = [];

    const ProgPageResponse = new Uint8Array([0x14, 0x10, 0x14, 0x10]); // sync - ok - sync - ok 
    this.#expectedResponse.push(...ProgPageResponse);

    // const half = Math.ceil(progPagePayload.length / 2);
    // const half = 9; // (half 1: 9 bytes, half 2: 128 bytes)
    // const half = 120; // (half 1: 120 bytes, half 2: 17 bytes)
    // let half = 120;
    // let MaxPageDelayMs = 8;
    let half = 0;
    let MaxPageDelayMs = 0;

    switch(this.getwriteMode()){
      case JDYUploader.#config.writeFastMode:
        half = 120; // (half 1: 120 bytes, half 2: 17 bytes)
        MaxPageDelayMs = 8;
        break;
      case JDYUploader.#config.writeSafeMode:
        half = 17; // (half 1: 17 bytes, half 2: 120 bytes)
        MaxPageDelayMs = 24;
        break;
    }

    let tSendH2Done = 0;
    let shouldUpdate = false;
    let isSendCompleted = false;
    let isRaceOngoing = true;

    const sendPayloadPromise = async () => {
      // await sleep(0); // yield
      await this.#serial.SerialPipe_sendToLeanbot( // send 120 bytes
        progPagePayload.subarray(0, half),
        false
      );
      this.#writePageTimeStamp[pageIndex].sendH1 = performance.now() - this.#writePageTimeStamp[pageIndex].startSend;
      console.log("Send Half 1 Done");
        // await sleep(0); // yield
      await this.#serial.SerialPipe_sendToLeanbot( // send 17 bytes
        progPagePayload.subarray(half),
        true
      );
      this.#writePageTimeStamp[pageIndex].sendH2 = performance.now() - this.#writePageTimeStamp[pageIndex].startSend;
      console.log("Send Half 2 Done");
      // this.#isCommandPayloadSendCompleted = true;
      isSendCompleted = true;

      tSendH2Done = performance.now();

      shouldUpdate = this.#shouldUpdateProgress(pageIndex);

      if(shouldUpdate){
        this.#emitUploadMessage(`Receive ${pageIndex + 1}`);
      }

      //rest until received all ack
      // await this.#waitUntil(() => this.#checkWritingPageDone(), JDYUploader.#config.writeFlashTimeoutMs, `Write page ${pageIndex} ack`);

      await sleep(MaxPageDelayMs);
    }

    const expectedIndexTarget = (pageIndex + 1) * 4;
    const waitResponsePromise = this.#waitUntil(
      () => (isSendCompleted && !(this.#lastExpectedIndex < expectedIndexTarget)) || this.#isResponseOutOfSync, 
      JDYUploader.#config.writeFlashTimeoutMs, 
      () => isSendCompleted
        ? `Write page ${pageIndex} wait response`
        : `Write page ${pageIndex} send payload`
    ).catch((e) => {
      if (e.message?.includes('timeout')) {
        this.#addToResponseRecords(e.message);
      }
      if (e.message?.includes('wait response timeout')) {
        this.#isResponseOutOfSync = true;
        return;
      }
      if (isRaceOngoing) {
        throw e;          // race: propagate để dừng upload ngay
      } else {
        this.abortAll(e); // orphan: signal các chỗ khác dừng
      }
    });

    // Race: full ACK received vs writeMode timeout.
    // #waitUntil returns true; sleep returns undefined — used to identify the winner.
    const ackFirst = await Promise.race([
      waitResponsePromise,
      sendPayloadPromise(),
    ]);
    isRaceOngoing = false;

    if (ackFirst) {
      console.log(`[WRITE] Page ${pageIndex}: received response first`);
    } else {
      const tRaceDone = performance.now();
      const tRealDelay = Math.round(tRaceDone - tSendH2Done);
      // Nếu real delay > 150% config delay thì thêm (*)
      console.log(
        `[WRITE] Page ${pageIndex}: delay first after ${tRealDelay} ms${
        tRealDelay > MaxPageDelayMs * 1.5
          ? " (*)"
          : ""
        }`
      );

      if(pageIndex === this.#uploader.totalPackets -1) await waitResponsePromise; // last page: block until ACK received before switching to verify
    }

    if(shouldUpdate){
      this.#emitUploadMessage(`Write ${(pageIndex + 1) * JDYUploader.#config.pageSizeByte} bytes`);
      // await sleep(0); // yield
    }
  }

  // // Handle the STK_PROG_PAGE acknowledgement response for write operations.
  // // This method accumulates incoming bytes until the final ACK sequence is received,
  // // then validates that the response contains a proper sync/OK pair before marking
  // // the write as complete.
  // #checkWritingPageDone() { // formal: async #handleWriteFlashAck2()

  //   // progPagePayload send completed (the final ACK should come after this) 
  //   // if(!this.#isCommandPayloadSendCompleted )return false;

  //   // at least one pair of (sync-ok) ACK received (the ACK frame should be at least 4 bytes: sync - ok - sync - ok)
  //   // if(this.#uploadResponseBuffer.length < 4)return false;

  //   const receivedLength = this.#uploadResponseBuffer.length;
  //   if(receivedLength < 4){
  //     // Not received 4 byte in 1 response, but received atleast 2 bytes.
  //     if(receivedLength >= 2)this.#writePageXStamp.received2 = performance.now() - this.#writePageXStamp.startSend; // t3
  //     return false;
  //   }

  //   // Received enough 4 bytes. Check if the t3 is marked or not, if not => received 4 bytes at once (t3 == t4)
  //   this.#writePageXStamp.received4 = performance.now() - this.#writePageXStamp.startSend;
  //   if(!this.#writePageXStamp.received2)this.#writePageXStamp.received2 = this.#writePageXStamp.received4;

  //   this.#writePageTimeStamp[this.#lastPageIndex] = {...this.#writePageXStamp}; // snapshot (not a reference)

  //   // the last byte MUST be OK for a valid ACK response
  //   if(!this.#isResponseOK(this.#uploadResponseBuffer[this.#uploadResponseBuffer.length - 1]))return false;

  //   // possible ...*(sync-ok - sync-ok)
  //   // check only the last 4 bytes to see if it contains a valid ACK pair (sync-ok - sync-ok)
  //   if(!this.#isACK(this.#uploadResponseBuffer, this.#uploadResponseBuffer.length - 4, 4)){
  //     console.log("[WRITE] Invalid PROG_PAGE ACK response");
  //     this.#emitUploadMessage("Write failed");
  //     throw new Error("Invalid PROG_PAGE ACK response"); // throw to stop upload
  //   }

  //   console.log("[WRITE] PROG_PAGE ACK received!");
  //   // console.log("[WRITE] PROG_PAGE ACK received!: ", this.#writePageTimeStamp[this.#lastPageIndex]);
  //   return true;
  // }

  #setwriteMode(newDelay){
    this.#writeMode = newDelay;
    localStorage.setItem(JDYUploader.#config.LS_KEY_WRITE_MODE, this.#writeMode);
    console.log("writeMode switch to: ", this.#writeMode);
  }

  /* ------------------- UPLOAD CODE ------------------- */
  async #uploadCode() {
    
    this.#lastPageIndex = -1; 

    console.log("[UPLOAD] Starting code upload...");
    const pages = this.#buildPagesFromBlocks(this.#BLEPackets, JDYUploader.#config.pageSizeByte);

    // Tổng số byte data thực sự (để UI dùng)
    const totalBytesData = pages.length * JDYUploader.#config.pageSizeByte;
    this.#uploader.totalPackets   = pages.length;
    this.#uploader.totalBytesData = totalBytesData;

    // await this.#uploader.onJDYWrite();

    this.#lastExpectedIndex = 0; // switch from getSync phase to write/read phase: start tracking responses from page 0
    const t1 = performance.now();
    try{
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];

        // await this.#writeFlash2(page.pageIndex, page.bytes);
        await this.#writeFlash3(page.pageIndex, page.bytes);

        if(this.#isResponseOutOfSync){
          const mismatchPage = Math.floor(this.#lastExpectedIndex / 4);
          await this.#waitUntilResponseResync();
          const resendIndex = mismatchPage * 4;
          this.#lastExpectedIndex = resendIndex; // reset về đầu page.
          this.#expectedResponse.length = resendIndex;  // TRUNCATE expect thừa (do page trước đã thêm vào rồi)
          i = mismatchPage - 1;
        }
      }
    }
    finally{
      const t2 = performance.now();
      const writeMs = t2 - t1;
      // compute BPS here: both totalBytesData and write duration are available
      this.#writeOnlyBps  = writeMs > 0 ? Math.round((totalBytesData * 1000) / writeMs) : 0;
      console.log(`[UPLOAD] Completed in ${sec3FromMs(writeMs)} s`);
    }

    await sleep(100); // delay 100ms before switch from write to verify

    // Verify
    const t3 = performance.now();
    // const verifyOk = await this.verifyUploadedCode();
    await this.verifyUploadedCode();
    const t4 = performance.now();
    console.log(`[UPLOAD] Verification completed in ${sec3FromMs(t4 - t3)} s`);
    // console.log("[UPLOAD] Verify result:", verifyOk ? "OK" : "FAILED");

    // Thông báo success / fail cho UI cũ
    // if (this.#uploader) {
    //   if (verifyOk) {
    //     this.#emitUploadMessage(`Upload success, Reset Leanbot`);
    //   } else {
    //     this.#emitUploadMessage(`Verify failed`);
    //     throw new Error("Verify failed");
    //   }
    // }
    this.#emitUploadMessage(`Upload success, Reset Leanbot`);
  }

  /* ------------------- BUILD PAGES FROM BLOCKS ------------------- */
  #buildPagesFromBlocks(mergedBlocks) {
    const pages = {};

    for (const block of mergedBlocks) {
      let addr = block.address;
      for (let i = 0; i < block.bytes.length; i++, addr++) {
        const pageIndex  = Math.floor(addr / JDYUploader.#config.pageSizeByte);
        const pageBase   = pageIndex * JDYUploader.#config.pageSizeByte;
        const pageOffset = addr % JDYUploader.#config.pageSizeByte;

        if (!pages[pageIndex]) {
          pages[pageIndex] = {
            pageIndex,
            address: pageBase,
            bytes: new Uint8Array(JDYUploader.#config.pageSizeByte).fill(0xFF),
          };
        }
        pages[pageIndex].bytes[pageOffset] = block.bytes[i];
      }
    }

    return Object.values(pages).sort((a, b) => a.pageIndex - b.pageIndex);
  }

  // /* ------------------- SO SÁNH 2 PAGE ------------------- */

  // // trả về payload thực sau khi loại bỏ overlap ACK,
  // // hoặc [] nếu frame không hợp lệ

  // #stripOverlapACK(page) {
  //   // read data = (sync ok)* - sync - data[] - ok
    
  //   const frameLength = JDYUploader.#config.pageSizeByte + 4;

  //   if (page.length < frameLength) return [];

  //   // frame hợp lệ phải kết thúc bằng OK
  //   if (!this.#isResponseOK(page[page.length - 1])) return [];

  //   // suy ngược vị trí INSYNC thật
  //   const syncIndex = page.length - frameLength + 2;

  //   // INSYNC không hợp lệ
  //   if (!this.#isResponseSync(page[syncIndex])) return [];

  //   // check luôn cả response của load address
  //   if(!this.#isACK(page, syncIndex - 2, 2))return [];

  //   // trả về payload thực
  //   return page.slice(syncIndex + 1, syncIndex + 1 + JDYUploader.#config.pageSizeByte);
  // }

  // #comparePage(expected, actual) {
  //   if (!expected || !actual) return false;
  //   if (expected.length !== actual.length) return false;

  //   for (let i = 0; i < expected.length; i++) {
  //     if (expected[i] !== actual[i]) return false;
  //   }
  //   return true;
  // }

  /* ------------------- VERIFY READ_PAGE ------------------- */
  /**
   * mode = "full"   → verify tất cả page
   * mode = "sample" → verify 1/16 số page, random
   * mode = "minimum" → verify 3 page, (page 0, random, page cuối)
   */
  async verifyUploadedCode(mode = "minimum") {
    const pages = this.#buildPagesFromBlocks(this.#BLEPackets, JDYUploader.#config.pageSizeByte);
    const totalPages = pages.length;

    if (totalPages === 0) {
      console.log("[VERIFY] No pages to verify.");
      return false;
    }

    let pageIndices = [];

    /* ---------------- SELECT VERIFY MODE ---------------- */
    if (mode === "full") {
      console.log(`[VERIFY] Mode FULL: verifying all ${totalPages} pages...`);
      pageIndices = [...Array(totalPages).keys()]; // 0 → totalPages-1

    } else if (mode === "sample") {
      const sampleCount = Math.max(1, Math.floor(totalPages / 16));
      const selected = new Set();

      while (selected.size < sampleCount) {
        const r = Math.floor(Math.random() * totalPages);
        selected.add(r);
      }

      pageIndices = [...selected];
      console.log(`[VERIFY] Mode SAMPLE: verifying ${pageIndices.length}/${totalPages} random pages (~1/16)`);
    } 
      else if (mode === "minimum") {
        // Verify 3 pages: first, random, last
        const selected = new Set();

        selected.add(0);
        selected.add(totalPages - 1);

        if (totalPages > 2) {
          const r = Math.floor(Math.random() * (totalPages - 2)) + 1;
          selected.add(r);
        }

        pageIndices = [...selected].sort((a, b) => a - b);

        console.log(
          `[VERIFY] Mode MINIMUM: verifying ${pageIndices.length}/${totalPages} pages (first, random, last)`
        );
      }

    else {
      console.log(`[VERIFY] Invalid mode "${mode}". Expected "full" or "sample".`);
      return false;
    }

    /* ---------------- EXECUTE VERIFY ---------------- */
    // let allOk = true;
    let verifyStep = 0; 
    this.#verifyStepIndex = 0; // reset before verify loop

    // await this.#uploader.onJDYRead();
    let readRetried = false;

    for (let i = 0; i < pageIndices.length; i++) {
      const idx = pageIndices[i];
      const page = pages[idx];
      const pageIndex = page.pageIndex;

      await sleep(0); // yield
      console.log(`[VERIFY] Reading page ${pageIndex}...`);
      // const deviceData = await this.#readPageAndGetData(pageIndex);

      // const normalizedData = this.#stripOverlapACK(deviceData);

      // const isSame = this.#comparePage(page.bytes, normalizedData);

      // try{
      //   await this.#readFlash2(pageIndex, page.bytes); // send read command and verify 
      // }
      // catch(e){
      //   console.log(`[VERIFY] Page ${pageIndex} MISMATCH!`);
      //   console.log(`[VERIFY] Verification FAILED (${mode} mode).`);
      //   this.#emitUploadMessage(`Verify failed`);
      //   throw e;
      // }

      await this.#readFlash2(pageIndex, page.bytes); // send read command and verify 
      if(this.#isResponseOutOfSync){
        this.#isResponseOutOfSync = false;
        if(readRetried) throw new Error(`Read page ${pageIndex}.${this.#verifyStepIndex} mismatched`); // đã retried tối thiểu 1 lần => throw luôn
        this.#expectedResponse.length = this.#lastExpectedIndex;  //  TRUNCATE expect thừa
        readRetried = true;
        i--;
        continue;
      }else{
        readRetried = false; // reset for later use
      }

      // if (!isSame) {
      //   allOk = false;
      //   console.log(`[VERIFY] Page ${pageIndex} MISMATCH!`);

      //   // log byte đầu tiên bị sai
      //   for (let i = 0; i < page.bytes.length; i++) {
      //     if (page.bytes[i] !== deviceData[i]) {
      //       console.log(
      //         `[VERIFY] Page ${pageIndex}, offset ${i}: ` +
      //         `expected 0x${page.bytes[i].toString(16).padStart(2,'0')}, ` +
      //         `got 0x${deviceData[i].toString(16).padStart(2,'0')}`
      //       );
      //       break;
      //     }
      //   }
      // } else {
      //   console.log(`[VERIFY] Page ${pageIndex} OK`);
      // }
      verifyStep++;
      this.#verifyStepIndex++; // advance after each successful readFlash2
      const verifiedBytes = (verifyStep / pageIndices.length) * this.#uploader.totalBytesData;
      this.#emitUploadMessage(`Verify ${Math.floor(verifiedBytes)} bytes`);
      await sleep(0); // yield
    }

    /* ---------------- FINAL RESULT ---------------- */
    // if (allOk) {
    //   console.log(`[VERIFY] Verification SUCCESS (${mode} mode).`);
    // } else {
    //   console.log(`[VERIFY] Verification FAILED (${mode} mode).`);
    // }

    // return allOk;
    console.log(`[VERIFY] Verification SUCCESS (${mode} mode).`);
  }

  #emitUploadMessage(message) {
    if (this.#uploader) {
      this.#uploader.onMessageInternal(message, false);
      const timeStamp = performance.now() - this.#uploadStartMs
      this.#uploader.onMessage({timeStamp, message});
    }
  }

  #shouldUpdateProgress(pageIndex) {
    if (!this.#uploader) return false;
    if (this.#uploader.totalPackets === 0) return false;

    if(pageIndex === this.#uploader.totalPackets - 1)return true; // always update when it's last page

    if(pageIndex  % JDYUploader.#config.UpdateStep === 0){
      return true;
    }

    return false;
  }

  /* Helpers */


  #isResponseSync(byte){
    return byte === 0x14; // sync
  }

  #isResponseOK(byte){
    return byte === 0x10; // ok
  }

  #isACKPair(bytes, index) {
    return this.#isResponseSync(bytes[index])
        && this.#isResponseOK(bytes[index + 1]);
  }

  #isACK(response, startIndex, length) {

    const start = startIndex;
    const end = startIndex + length;
    for (let i = start; i < end; i += 2) {
      if(!this.#isACKPair(response, i))return false;
    }

    return true;
  }

  elapsedTimeMs() {
    // if(this.#isPreUpload)return 0; // pre-upload => always return 0
    if(this.#isPreUpload)return this.getTotalTimeMs(); // pre upload => return time from start of jdy enter bootloader until failed
    if (this.#uploadEndMs === 0) // if upload not done yet
      return performance.now() - this.#uploadStartMs;
    return this.#uploadEndMs - this.#uploadStartMs; // upload already done
  }

  EnterBootloaderTimeMs() {
    if (this.#enterBoothEndMs === 0)
      return performance.now() - this.#enterBoothStartMs;
    return this.#enterBoothEndMs - this.#enterBoothStartMs; 
  }

  getTotalTimeMs(){
    if (this.#CompileAndUploadEndMs === 0)
      return performance.now() - this.#CompileAndUploadStartMs;
    return this.#CompileAndUploadEndMs - this.#CompileAndUploadStartMs; 
  }

  getUploadWriteBps() {
    return this.#writeOnlyBps;
  }

  getUploadStastics(){

    const sendH1Array    = this.#writePageTimeStamp.map(x => x.sendH1);
    const sendH2Array    = sendH1Array.map((h1, i) => this.#writePageTimeStamp[i].sendH2 - h1); // sendH2 - sendH1
    const received2Array = sendH1Array.map((h1, i) => this.#writePageTimeStamp[i].received2 - h1); // received2 - sendH1
    const received4Array = sendH2Array.map((h2, i) => this.#writePageTimeStamp[i].received4 - h2); // received4 - sendH2

    const statsSendH1 = statsCalculate(sendH1Array);
    const statsSendH2 = statsCalculate(sendH2Array);
    const statsReceived2 = statsCalculate(received2Array);
    const statsReceived4 = statsCalculate(received4Array);

    return {
      sendH1: statsSendH1,
      sendH2: statsSendH2,
      received2: statsReceived2,
      received4: statsReceived4,
    }
  }

  getwriteMode(){
    // return this.#writeMode;
    return JDYUploader.#config.writeFastMode;
  }

  getwriteReadResponseRecords() {
    // Array đã newest-first (do unshift) — serialize trực tiếp, không cần reverse
    return this.#writeReadResponseRecords
      .map(({ phase, page, errorType, responseInfo, isReSync }) => {
        const prefix  = phase[0].toLowerCase();   // 'w' | 'r'
        const pageSuffix = (errorType.toLowerCase() === 'send payload timeout' ? '.p' : '') + (isReSync ? '.s' : '');
        return `${prefix}${page}${pageSuffix} ${responseInfo}`;
      })
      .join('\n');
  }

  abortAll(reason){

    const abortReason = reason ?? 'Unknown reason';
    console.log('[JDYUploader] Abort jdy upload, reason: ', abortReason);

    this.#uploadAbortController?.abort(abortReason);
    this.isUploading = false;             
  }
}

function bytesToHex(bytes) {

  if (!bytes || bytes.length === 0) {
    return "";
  }

  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ');
}

function numberToHex(value, length = 2) {
  return (value >>> 0).toString(16).toUpperCase().padStart(length, '0');
}

function shortenArray(array, len = 13) {
  if (!Array.isArray(array)) return '[]';

  return array.length > len
    ? `[${bytesToHex(array.slice(0,6))} ... ${bytesToHex(array.slice(array.length - 6))}]`
    : `[${bytesToHex(array)}]`;
}

// ======================================================
// 🔹 RETRY Statistic write page X
// ======================================================

function statsCalculate(arr) {
  // filter out undefined entries (e.g. received2 is not always captured)
  const valid = arr.filter(x => x !== undefined && !isNaN(x));
  if (valid.length === 0) return { max: null, avg: null, stdev: null };

  const max = Math.max(...valid);

  const avg = valid.reduce((sum, x) => sum + x, 0) / valid.length;

  const variance =
    valid.reduce((sum, x) => sum + (x - avg) ** 2, 0) / valid.length;

  const stdev = Math.sqrt(variance);

  const round = n => Math.round(n * 1000) / 1000;

  return {
    max: round(max),
    avg: round(avg),
    stdev: round(stdev),
  };
}

// ======================================================
// 🔹 RETRY HELPER FUNCTION*-+*-
// ======================================================

/**
 * @function retryUntilTimeout
 * @description
 * Repeatedly calls an async function until it returns a truthy value
 * or a timeout occurs.
 *
 * Falsy results trigger a retry after an optional delay.
 * Throws a timeout error if the timeout is reached, or propagates
 * the original error thrown by asyncFunc without modification.
 *
 * Timeout errors created by this function have:
 *   e.type = "retryUntilTimeout.Timeout"
 *
 * @param {Function} asyncFunc - Async function that returns a Promise.
 *   Receives retry count (starting at 1).
 *   truthy → success, stop retrying
 *   falsy → retry
 *
 * @param {string} label - Label used in the timeout error message.
 * @param {number} timeoutMs - Max time allowed before timeout error.
 * @param {number} [retryInvervalMs=0] - Delay between retries.
 *
 * @returns {Promise<{success: boolean, retry: number}>}
 * @throws {Error} Timeout error with e.type = "retryUntilTimeout.Timeout",
 *   or the original error thrown by asyncFunc.
 */

async function retryUntilTimeout(asyncFunc, label, timeoutMs, retryInvervalMs = 0) {

  if (typeof asyncFunc !== "function") return false;

  let retry = 0;

  const asyncFuctionWithDelay = async () => {
    retry++;
    const result = await asyncFunc(retry);
    if (result) return true;
    await sleep(retryInvervalMs);
    return false;
  };

  const timeout = sleep(timeoutMs);

  while (true) {

    const result = await Promise.race([
      asyncFuctionWithDelay(),
      timeout, // timeout
    ]);

    if (result === undefined) {
      const error = new Error(`${label} timeout after ${timeoutMs} ms`);
      error.type = "retryUntilTimeout.Timeout";
      throw error;
    }

    if (result === true) { // only true return can escape
      return { success: true, retry: retry };
    }
  }
}

// ======================================================
// 🔹 HEX TO BLE PACKETS CONVERTER
// ======================================================
function parseHexLine(LineMessage) {
  if (!LineMessage.startsWith(":")) return null;
  const hex = LineMessage.slice(1);
  const length = parseInt(hex.substr(0, 2), 16);
  const address = parseInt(hex.substr(2, 4), 16);
  const recordType = hex.substr(6, 2);
  const data = hex.substr(8, length * 2);
  const checksum = parseInt(hex.substr(8 + length * 2, 2), 16);
  return { length, address, recordType, data, checksum, hex };
}

// Kiểm tra checksum của dòng HEX
function verifyChecksum(parsed) {
  const { hex, length, checksum } = parsed;
  const allBytes = [];
  for (let i = 0; i < 4 + length; i++) {
    allBytes.push(parseInt(hex.substr(i * 2, 2), 16));
  }
  const sum = allBytes.reduce((a, b) => a + b, 0);
  const calcChecksum = ((~sum + 1) & 0xFF);
  return calcChecksum === checksum;
}

// Chuyển dòng HEX thành mảng byte
function hexLineToBytes(block) {
  const bytes = [];
  for (let i = 0; i < block.length; i += 2) {
    const b = parseInt(block.substr(i, 2), 16);
    if (!isNaN(b)) bytes.push(b);
  }
  return new Uint8Array(bytes);
}

/**
 * Convert Intel HEX text into optimized BLE packets
 * - Parse HEX LinesMessage → validate checksum
 * - Merge consecutive LinesMessage with continuous addresses
 * - Split into BLE packets of max 236 bytes
 * 
 * @param {string} hexText - HEX file content
 * @returns {Uint8Array[]} packets - Array of BLE message bytes ready to send
 */
function convertHexToBlePackets(hexText, { returnStep2 = false } = {}) {
  const BLE_MaxLength = Uploader.getBLE_MaxLength();
  console.log(`convertHexToBlePackets: Using BLE_MaxLength = ${BLE_MaxLength}`);

  // --- STEP 0: Split HEX text into LinesMessage ---
  const LinesMessage = hexText.split(/\r?\n/).filter(LineMessage => LineMessage.trim().length > 0);

  // --- STEP 1: Parse each HEX LineMessage ---
  const parsedLines = [];
  for (let i = 0; i < LinesMessage.length; i++) {
    const parsed = parseHexLine(LinesMessage[i].trim());
    if (!parsed) continue;
    if (!verifyChecksum(parsed)) continue;
    const bytes = hexLineToBytes(parsed.data);
    parsedLines.push({ address: parsed.address, bytes: bytes });
  }

  // --- STEP 2: Merge consecutive address blocks ---
  const mergedBlocks = [];
  let current = null;

  for (const LineMessage of parsedLines) {
    if (!current) {
      // Dùng spread operator [...] để sao chép dữ liệu, tránh ảnh hưởng mảng gốc
      current = { address: LineMessage.address, bytes: [...LineMessage.bytes] };
      continue;
    }

    const expectedAddr = current.address + current.bytes.length;
    if (LineMessage.address === expectedAddr) {
      current.bytes.push(...LineMessage.bytes);
    } else {
      mergedBlocks.push(current);
      current = { address: LineMessage.address, bytes: [...LineMessage.bytes] };
    }
  }
  if (current) mergedBlocks.push(current);

  if (returnStep2) {
    console.log("convertHexToBlePackets: returning mergedBlocks after STEP 2");
    return mergedBlocks;
  }

  // --- STEP 3: Split each merged block into BLE packets (≤ BLE_MaxLength bytes) ---
  const packets = [];
  let sequence = 0;
  let lastAddr = 0;

  for (const block of mergedBlocks) {
    const data = block.bytes;
    const isLastBlock = block === mergedBlocks[mergedBlocks.length - 2]; // block EOF không tính

    // Tính delta giữa các block (so với block trước)
    let deltaAddr = 0;

    if (packets.length === 0) {
      deltaAddr = 0; // block đầu tiên
    } else {
      const diff = block.address - lastAddr;
      while (diff > 0x7F) {
        // Gửi marker 0x7F (bản tin rỗng)
        const seqByte = sequence & 0xFF;
        const marker = new Uint8Array([seqByte, 0x7F]);
        packets.push(marker);
        sequence++;
        diff -= 0x7F; // giảm dần khoảng cách
      }

      deltaAddr = diff & 0x7F; // giới hạn trong [0x00, 0x7F]
    }
    
    let offset = 0;

    while (offset < data.length) {
      const remain = data.length - offset;

      const isFinalPacket = isLastBlock && (offset + (BLE_MaxLength - 1) >= data.length);

      if (deltaAddr === 0 && remain >= (BLE_MaxLength - 1)) {
        // Loại 1: [Seq][511 data]
        const chunk = data.slice(offset, offset + (BLE_MaxLength - 1));
        const bytes = new Uint8Array([sequence & 0xFF, ...chunk]);
        packets.push(bytes);
        offset += (BLE_MaxLength - 1);
      } else {
        // Loại 2: [Seq][deltaAddr][≤509 data]
        const chunk = data.slice(offset, offset + (BLE_MaxLength - 3));
        const effectiveDelta = isFinalPacket ? (0xFF - deltaAddr) : deltaAddr;
        const bytes = new Uint8Array([sequence & 0xFF, effectiveDelta, ...chunk]);
        packets.push(bytes);
        offset += (BLE_MaxLength - 3);
      }

      sequence++;
    }

    lastAddr = block.address + data.length;
  }
  return packets;
}

// ======================================================
// 🔹 HASH FUNCTION (32-bit)
// ======================================================

/**
 * 
 * @param {*} x: key Hash (uint32)
 * @returns hash32 (uint32)
 */

function hash_prospector(x) {
    x ^= x >>> 16;
    x = Math.imul(x, 0x21f0aaad);
    x ^= x >>> 15;
    x = Math.imul(x, 0xd35a2d97);
    x ^= x >>> 15;
    return x;
}

/**
 * @param {number} hash - hash hiện tại
 * @param {number} data - dữ liệu 32-bit
 * @returns {number} hash mới (uint32)
 */
function updateHash(hash, data) {
  hash ^= data;
  hash = hash_prospector(hash)
  return hash >>> 0; // đảm bảo trả về uint32
}

/**
 * @param {number} hash32 - hash hiện tại (uint32)
 * @param {Uint8Array} data - mảng byte
 * @returns {number} hash sau khi xử lý hết data
 */
function updateHashWithBytes(hash32, data) {
  let idx = 0;
  const len = data.length;

  // Xử lý các block 4 byte
  const fullBlocks = (len / 4) | 0;
  for (let i = 0; i < fullBlocks; i++) {
    let data32 =
      (data[idx]       << 0)  |
      (data[idx + 1]   << 8)  |
      (data[idx + 2]   << 16) |
      (data[idx + 3]   << 24);

    hash32 = updateHash(hash32, data32);
    idx += 4;
  }

  // Xử lý phần còn lại 1–3 byte
  if (idx < len) {
    let data32 = 0;
    for ( ; idx < len; idx++) {
      data32 <<= 8;
      data32  |= data[idx];
    }
    hash32 = updateHash(hash32, data32);
  }

  return hash32;
}

// ======================================================
// 🔹 Console log timeStamp
// ======================================================

function sec3FromMs(ms) {
  return (ms / 1000).toFixed(3);
}